import type { SagaIterator } from 'redux-saga';
import type { StrictEffect } from 'redux-saga/effects';
import { put, delay, select, call } from 'redux-saga/effects';
import { call as typedCall, select as typedSelect, all as typedAll } from 'typed-redux-saga';
import {
  selectUserId,
  selectProfileName,
  compareDesc,
  selectProfileOrganizationId,
  storeSessionCaseFilter,
} from 'flowhawk-core';
import { inviteNewUserSuccess, inviteNewUserFailed, addSingleUserToChatroomFailed } from '../Redux/ChatroomActions';
import { apiFetch, apiPost, apiPut, apiDelete, apiCachedFetch, apiPatch } from './ApiSaga';
import {
  CHAT_TAB,
  DONOR_STATUS_VIEW,
  DONOR_TASK_VIEW,
  pushError,
  selectDonorView,
  selectTab,
  setSagaMessage,
} from '../Redux/ApplicationActions';
import api from '../Services/Api';
import { refetchCaseViews } from '../Utils/hooks';
import { finishLoading, finishLoadingEntity, startLoading, startLoadingEntity } from '../Redux/LoadingActions';
import { loadMessages, openChatroom } from '../Redux/ChatListActions';
import {
  createDonorFailed,
  importDonorDataSuccess,
  importDonorDataFailed,
  receiveDonors,
  updateDonorSuccess,
  receiveDonorFiles,
  uploadDonorFileSuccess,
  uploadDonorFileFailed,
  deleteDonorFileSuccess,
  deleteDonorFileFailed,
  loadDonorFiles,
  resetDonorEditState,
  receiveOrganizationMembers,
  openFollowerGroup,
  receiveDonorFollowerGroups,
  receiveSingleDonor,
  receiveDonorTask,
  getDonorTaskData,
  receiveDonorTaskData,
  createDonorSuccess,
  receivePdfFields,
  updateFollowerGroupPermissions,
  updateFollowerGroupTasks,
  updateFollowerGroupMembers,
  receiveCaseTimeline,
  setCasePreferences,
  receiveTotalFilteredCases,
  setCaseDisplayOrder,
  receiveEditCaseTaskStatus,
  donorChanged,
  setEditCaseTaskStatus,
  createCaseViewSuccess,
  createCaseViewFailed,
  updateCaseViewSuccess,
  updateCaseViewFailed,
  deleteCaseViewSuccess,
  deleteCaseViewFailed,
  setWorkflowKey,
  getDonor,
} from '../Redux/DonorActions';
import type {
  LoadDonorFiles,
  UploadDonorFile,
  DeleteDonorFile,
  CreateDonor,
  ImportDonorData,
  UpdateDonorStatus,
  UpdateDonor,
  GetDonor,
  UpdateTaskStatus,
  GetDonorTask,
  GetDonorTaskData,
  UpdateTaskDueDate,
  UpdateTaskNote,
  UpdateDonorForm,
  DeleteFollowerGroup,
  UpdateFollowerGroupTasks,
  UpdateFollowerGroupPermissions,
  UpdateFollowerGroupMembers,
  GetFollowerGroup,
  GetDonorFollowerGroups,
  GetCaseTimeline,
  LoadCasePreferences,
  LoadCases,
  PatchUpdateCase,
  GenerateModularCasePDF,
  SetEditCaseTaskStatus,
  PinCase,
  UnpinCase,
  CreateCaseView,
  UpdateCaseView,
  DeleteCaseView,
} from '../Redux/DonorActions';
import { initFollowerEditData, resetFollowerEditData } from '../Redux/FollowerEditActions';
import { setSelectedUsers, setAddPeopleError } from '../Redux/AddPeopleActions';
import type {
  CreateFollowerGroup,
  CreateMultipleFollowerGroups,
  FollowerEditState,
  RemoveUserFromFollowers,
  UpdateFollowerGroup,
  InviteNewUser,
  JoinFollowerGroup,
  DeleteFollowerInvite,
} from '../Redux/FollowerEditActions';
import type { UpdateAlertStatusRequest, CreateCaseServiceInfoRoomRequest } from '../Redux/CaseAlertsActions';
import {
  updateAlertStatusSuccess,
  updateAlertStatusFailure,
  createCaseServiceInfoRoomSuccess,
  createCaseServiceInfoRoomFailure,
} from '../Redux/CaseAlertsActions';
import type { AddPeopleByEmail, AddPeopleByPhone, AddPeopleInvite } from '../Redux/AddPeopleActions';
import parseProfile, { parseExternalUser, parseFollowerInvite } from '../Utils/profile';
import { MessageTypes, type MessageType } from '../Utils/types';
import { keys, values } from '../Utils/Object';
import { serializePhone } from '../Utils/serializePhone';
import type {
  DonorDataEntry,
  FollowerGroup,
  FormValueMap,
  Donor,
  FormDefData,
  UserProfile,
  Organ,
  Task,
  MessageId,
  FileMap,
  TaskDataArrayItem,
  FollowerGroupMap,
  AppFile,
  TaskDataMap,
  CaseView,
  DonorSource,
} from '../Utils/types';
import hasValue from '../Utils/hasValue';
import formIndexMap from '../Redux/Forms/FormIndexMap';
import { formatDate, isoNowUTC, formatDateTimeToTZ } from '../Utils/time';
import { bulkReceiveFormData, formChanged, setFormErrors } from '../Redux/FormActions';
import { parseError, parseResponseTextError } from '../Utils/flattenError';
import { convertToTag } from '../Utils/tags';
import { sendCaseCommandNoResult } from './Socket';
import { convertToCaseNote } from '../Utils/caseNotes';
import devLog from '../Utils/logger';
import type {
  ApiCase,
  ApiCaseServiceAlert,
  ApiCaseTaskHistory,
  ApiGetCaseTimelineResponse,
  ApiContactsResponse,
  ApiCreateCaseResponse,
  ApiDeleteDonorFileResponse,
  ApiDonorFileUploadResponse,
  ApiDonorImportFieldResponse,
  ApiEditCaseTaskResponse,
  ApiFollowerDetails,
  ApiGenerateModulePdfResponse,
  ApiGetCaseFilesResponse,
  ApiGetCaseFollowerGroupsDetailedResponse,
  ApiGetCaseFollowerGroupsResponse,
  ApiGetCasesResponse,
  ApiGetCaseTasksResponse,
  ApiGetOrganizationMemberResponse,
  ApiInviteUserRequest,
  ApiInviteUserResponse,
  ApiPartialFollowerGroup,
  ApiUpdateCaseStatusResponse,
  ApiUpdateFollowerPermissionsResponse,
  ApiUpdateFollowerTasksResponse,
  ApiUpdateFollowerUsersResponse,
  ApiDeleteFollowerInviteResponse,
  ApiUserPublicProfile,
  ApiWorkflowAndForms,
} from '../Utils/apiTypes';
import { InvalidResponseCode, ResourceErrorInterface } from 'tg-resources';
import type { RootState } from '../Redux';
import { router } from '..';
import { getXmatCaseFiles } from '../Utils/caseFiles';

function* retrieveSingleDonorData(donorId: number) {
  const { result, error } = yield* typedCall(apiFetch<ApiCase>, api.txp.getDonor, { donorId });

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        if (error.statusCode === 404) {
          yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
          router.navigate({ to: '/cases' });
        }
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Loading case failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Loading case failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Loading case failed', 'Try again later', ''));
    }
    yield put(finishLoading('donor'));
  } else {
    const donId = result.donor_id;

    const organsArray: Organ[] = [];
    for (let i = 0; i < result.organs.length; i += 1) {
      organsArray.push({
        organId: result.organs[i].organ_id,
        organType: result.organs[i].organ_type,
      });
    }

    const taskArray: Task[] = [];
    for (let i = 0; i < result.tasks.length; i += 1) {
      taskArray.push({
        taskId: result.tasks[i].task_id,
        completed: result.tasks[i].completed,
        notApplicable: result.tasks[i].not_applicable,
        hasNoteContent: result.tasks[i].has_note_content,
        hasTaskData: result.tasks[i].has_task_data || false,
        lastModified: result.tasks[i].last_modified,
        lastModifiedBy: result.tasks[i].last_modified_by,
        dueDate: result.tasks[i].due_date,
        dueDateInterval: result.tasks[i].due_date_interval,
        dueDateComment: result.tasks[i].due_date_comment,
        previousTaskId: result.tasks[i].previous_task_id,
        userCanRead: result.tasks[i].user_can_read,
        rights: result.tasks[i].rights,
        editBy: result.tasks[i].edit_by,
        editTime: result.tasks[i].edit_time,
      });
    }
    let dob = '';
    if (result && result.date_of_birth && result.date_of_birth.length >= 10) {
      dob = result.date_of_birth.substring(0, 10);
    }

    const donorData = yield* typedSelect((state: RootState) => state.donor.donors[donId]);
    const files = donorData && donorData.files ? donorData.files : {};
    const fileIds = donorData && donorData.fileIds ? donorData.fileIds : [];
    const pendingFiles = donorData && donorData.pendingFiles ? donorData.pendingFiles : {};

    const lastModifiedTaskId = donorData && donorData.lastModifiedTaskId ? donorData.lastModifiedTaskId : undefined;
    const lastModifiedTaskDate =
      donorData && donorData.lastModifiedTaskDate ? donorData.lastModifiedTaskDate : undefined;
    const existingPinNumber = yield* typedSelect((state: RootState) => state.donor.donors[donId]?.pinNumber) || null;
    const xmatFiles: FileMap = getXmatCaseFiles(result.xmat_case_id ?? '');

    const donor: Donor = {
      ...donorData,
      donorId: donId,
      opoDonorId: result.opo_donor_id,
      highRisk: result.high_risk,
      unosId: result.unos_id || '',
      donorSource: result.donor_source || undefined,
      userId: result.user_id,
      orgId: result.organization_id,
      createDate: result.create_date,
      closed: result.closed || false,
      organs: organsArray,
      currentLocation: result.current_location || '',
      dob,
      race: result.race || '',
      sex: result.sex || '',
      typeOfDeath: result.type_of_death || '',
      causeOfDeath: result.cause_of_death || '',
      height: hasValue(result.height) ? result.height.toString() : '',
      weight: hasValue(result.weight) ? result.weight.toString() : '',
      unetLink: result.unet_link || '',
      tasks: taskArray,
      lastModified: result.last_modified,
      lastModifiedBy: result.last_modified_by,
      workflow: result.workflow,
      lastModifiedTaskId,
      lastModifiedTaskDate,
      files,
      fileIds,
      pendingFiles,
      xmatFiles,
      tags: result.tags ? result.tags.map((t) => convertToTag(t)) : [],
      notes: result.notes ? result.notes.map((note) => convertToCaseNote(note)) : [],
      unread: result.unread || 0,
      stage: result.stage,
      rank: result.rank,
      sequenceNumber: result.sequence_number,
      pinNumber: existingPinNumber,
      initialClose: result.initial_close,
      alerts: result.alerts ?? [],
      hasNewServiceAlert: result.has_new_service_alert,
      isAdmin: result.is_admin,
      rights: result.rights,
      xmatCaseId: result.xmat_case_id,
      perfusionDeviceSn: result.perfusion_device_sn,
      donorAttachments: result.donor_attachments,
      caseAdmins: result.case_admins,
      orgPermission: result.org_permission,
    };

    yield put(receiveSingleDonor(donId, donor));
  }
}

export function* loadCasesSaga(action: LoadCases): SagaIterator<void> {
  yield delay(100); // Allow for debounce of multiple calls

  const { params } = action;
  yield put(startLoading('cases'));

  const { result, error } = yield* typedCall(apiPost<ApiGetCasesResponse>, api.txp.cases, {}, { ...params });

  const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors);
  const openDonorId = yield* typedSelect((state: RootState) => state.donor.openDonorId);

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        const errorMessage = parseResponseTextError(error.responseText);
        yield put(setSagaMessage('', `${errorMessage}`, ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Loading cases failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Loading cases failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Loading cases failed', 'Try again later', ''));
    }
    yield put(finishLoading('cases'));
  } else {
    const casesArray = result.cases;
    const totalFilteredCases = parseInt(result.total_cases_count, 10);
    const caseDisplayOrder: number[] = [];

    // Retain currently open donor for instances when navigating to a donor
    // before donors have been loaded
    const cases = openDonorId ? { [openDonorId]: currentDonors[openDonorId] } : {};

    for (let i = 0; i < casesArray.length; i += 1) {
      caseDisplayOrder.push(casesArray[i].donor_id);
      const organsArray = casesArray[i].organs;
      const organs = [];
      const currentDonor = currentDonors[casesArray[i].donor_id] || {};
      const xmatFiles: FileMap = getXmatCaseFiles(casesArray[i].xmat_case_id ?? '');

      for (let j = 0; j < organsArray.length; j += 1) {
        organs.push({
          organId: organsArray[j].organ_id,
          organType: organsArray[j].organ_type,
        });
      }
      let dob = '';
      if (casesArray[i].date_of_birth && casesArray[i].date_of_birth.length >= 10) {
        dob = casesArray[i].date_of_birth.substring(0, 10);
      }

      // Build our task objects
      const tasks = (casesArray[i].tasks || []).map((task) => ({
        completed: task.completed,
        description: task.description,
        dueDate: task.due_date,
        dueDateInterval: task.due_date_interval,
        dueDateComment: task.due_date_comment,
        hasNoteContent: task.has_note_content,
        hasTaskData: task.has_task_data,
        lastModified: task.last_modified,
        lastModifiedBy: task.last_modified_by,
        notApplicable: task.not_applicable,
        previousTaskId: task.previous_task_id,
        taskId: task.task_id,
        userCanRead: task.user_can_read,
        rights: task.rights,
        editBy: task.edit_by,
        editStart: task.edit_time,
      }));

      cases[casesArray[i].donor_id] = {
        ...currentDonor,
        donorId: casesArray[i].donor_id,
        opoDonorId: casesArray[i].opo_donor_id,
        highRisk: casesArray[i].high_risk,
        unosId: casesArray[i].unos_id,
        opo: casesArray[i].opo,
        matchId: casesArray[i].match_id,
        orgId: casesArray[i].organization_id,
        userId: casesArray[i].user_id,
        createDate: casesArray[i].create_date,
        closed: casesArray[i].closed,
        currentLocation: casesArray[i].current_location || '',
        dob,
        sex: casesArray[i].sex || '',
        organs,
        lastModified: casesArray[i].last_modified,
        lastModifiedBy: casesArray[i].last_modified_by,
        lastModifiedTaskDate: casesArray[i].last_modified_task_date,
        lastModifiedTaskId: casesArray[i].last_modified_task_id,
        workflow: casesArray[i].workflow,
        tags: casesArray[i].tags ? casesArray[i].tags.map((t) => convertToTag(t)) : [],
        notes: casesArray[i].notes ? casesArray[i].notes.map((note) => convertToCaseNote(note)) : [],
        tasks,
        isAdmin: casesArray[i].is_admin,
        rights: casesArray[i].rights,
        unread: casesArray[i].unread ? casesArray[i].unread : 0,
        stage: casesArray[i].stage,
        rank: casesArray[i].rank,
        sequenceNumber: casesArray[i].sequence_number,
        initialClose: casesArray[i].initial_close,
        pinNumber: casesArray[i].pin_number || null,
        hasNewServiceAlert: casesArray[i].has_new_service_alert || null,
        alerts: casesArray[i].alerts ?? [],
        xmatCaseId: casesArray[i].xmat_case_id,
        perfusionDeviceSn: casesArray[i].perfusion_device_sn,
        xmatFiles,
      };
    }

    yield put(receiveDonors(cases));
    yield put(receiveTotalFilteredCases(totalFilteredCases));
    yield put(setCaseDisplayOrder(caseDisplayOrder));
    yield put(finishLoading('cases'));
  }
}

export function* getDonorSaga(action: GetDonor) {
  const { donorId } = action;

  yield put(startLoading('donor'));
  yield retrieveSingleDonorData(donorId);
  yield put(finishLoading('donor'));
}

export function* updateDonorStatusSaga(action: UpdateDonorStatus): SagaIterator<void> {
  const { donorId, closed, lastModified } = action;

  const { result, error } = yield* typedCall(
    apiPut<ApiUpdateCaseStatusResponse>,
    api.txp.updateDonorStatus,
    { donorId },
    { closed, last_modified: lastModified },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(pushError(parseResponseTextError(error.responseText)));
      } else if (error.isNetworkError) {
        yield put(
          pushError(closed ? 'Closing case failed, are you online?' : 'Reopening case failed, are you online?', error),
        );
      } else {
        yield put(
          pushError(closed ? 'Closing case failed, try again later' : 'Reopening case failed, try again later', error),
        );
      }
    } else {
      yield put(
        pushError(closed ? 'Closing case failed, try again later' : 'Reopening case failed, try again later', error),
      );
    }
  } else {
    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors || {});

    currentDonors[donorId] = {
      ...currentDonors[donorId],

      closed,
      initialClose: currentDonors[donorId].initialClose ?? new Date().toISOString(),
      lastModified: result.last_modified,
    };

    yield put(updateDonorSuccess(donorId, currentDonors[donorId]));

    const toastMessage = closed ? 'Case closed!' : 'Case reopened!';
    yield put(setSagaMessage('', toastMessage, ''));
  }
}

export function* updateAlertStatusSaga(action: UpdateAlertStatusRequest): SagaIterator<void> {
  const { organizationAlertId, donorId, status, createChatroom } = action;

  yield put(startLoading('updateAlert'));

  const { result, error } = yield* typedCall(
    apiPut<ApiCaseServiceAlert>,
    api.txp.updateOrganizationAlert,
    { organizationAlertId },
    { status, create_chatroom: createChatroom },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(updateAlertStatusFailure(parseResponseTextError(error.responseText)));
        yield put(pushError(parseResponseTextError(error.responseText)));
      } else if (error.isNetworkError) {
        yield put(updateAlertStatusFailure('Network error'));
        yield put(pushError('Updating alert failed, are you online?', error));
      } else {
        yield put(updateAlertStatusFailure('Unknown error'));
        yield put(pushError('Updating alert failed, try again later', error));
      }
    } else {
      yield put(updateAlertStatusFailure('Unknown error'));
      yield put(pushError('Updating alert failed, try again later', error));
    }
    yield put(finishLoading('updateAlert'));
  } else {
    yield put(updateAlertStatusSuccess(result, donorId));

    if (createChatroom && result.chatroomId) {
      yield put(openChatroom(result.chatroomId));
      yield put(selectTab(CHAT_TAB));
    }

    const successMessage = createChatroom ? 'Alert acknowledged and chat room created!' : 'Alert updated!';
    yield put(setSagaMessage('', successMessage, ''));
    yield put(finishLoading('updateAlert'));
  }
}

export function* createCaseServiceInfoRoomSaga(action: CreateCaseServiceInfoRoomRequest): SagaIterator<void> {
  const { caseId, providerServiceId } = action;

  yield put(startLoading('createServiceInfoRoom'));

  try {
    // API call to create a service info room
    const { result, error } = yield* typedCall(
      apiPost<{ chatroomId: number }>,
      api.txp.createCaseServiceInfoRoom,
      {
        providerId: providerServiceId,
      },
      {
        caseId,
      },
    );

    if (error || !result) {
      yield put(createCaseServiceInfoRoomFailure('Failed to create service info room'));
      yield put(setSagaMessage('', 'Failed to create service info room', ''));
    } else {
      // Success
      yield put(createCaseServiceInfoRoomSuccess(caseId, result.chatroomId));
      yield put(openChatroom(result.chatroomId));
      yield put(selectTab(CHAT_TAB));
      yield put(setSagaMessage('', 'Service info room created successfully!', ''));
    }
  } catch (error) {
    const newError = error instanceof Error ? error : new Error('Unknown upload error');
    yield put(pushError('Failed to create service info room', newError));
    yield put(createCaseServiceInfoRoomFailure('Failed to create service info room'));
    yield put(setSagaMessage('', 'Failed to create service info room', ''));
  }

  yield put(finishLoading('createServiceInfoRoom'));
}

export function* createDonorSaga(action: CreateDonor) {
  const {
    opoDonorId,
    workflowKey,
    highRisk,
    tags,
    hospital,
    sex,
    dateOfBirth,
    organs,
    importedDonorData,
    existingDonorId,
    selectedProviderServices,
    stage,
    isUnosImport,
    donorNetPdf,
  } = action;
  let { unosId } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('saveDonor'));

  let processedImportedDonorData: DonorDataEntry[] = [];
  let timeOfDeathString = '';
  let race = '';
  let bloodType = '';
  let height = null;
  let weight = null;
  let causeOfDeath = '';
  let typeOfDeath = '';

  // We Timezone and DST to properly construct timestamps for imported pdf data
  // Don't use imported data if instead passing in existingDonorId
  if (importedDonorData?.length && !existingDonorId) {
    const timezoneImport = importedDonorData.find((data) => data.key === 'time_zone');
    const dstImport = importedDonorData.find((data) => data.key === 'dst_observed');

    // Convert date fields into ISO strings if timezone and daylight savings information available
    if (timezoneImport && dstImport) {
      const tz = timezoneImport.value;
      const dst = dstImport.value.toLowerCase() === 'yes';

      processedImportedDonorData = importedDonorData.map((entry) => {
        if (entry.key.endsWith('_date')) {
          if (entry.key === 'or_date') {
            // OR Date can end with 'Scheduled' or 'Tentative' and cause the date to be invalid, try to trim those
            entry.value = entry.value.replace(/scheduled|tentative/gi, '').trim();
          }
          entry.value = entry.value ? formatDateTimeToTZ(entry.value, tz, dst) : '';
        }

        return entry;
      });
    } else {
      processedImportedDonorData = importedDonorData;
    }

    const timeOfDeathImport = processedImportedDonorData.find((data) => data.key === 'death_date');
    if (timeOfDeathImport) {
      timeOfDeathString = timeOfDeathImport.value;
    }

    const unosImport = processedImportedDonorData.find((data) => data.key === 'unos_id');
    if (unosImport) {
      unosId = unosImport.value;
    }

    const raceImport = processedImportedDonorData.find((data) => data.key === 'race');
    if (raceImport) {
      race = raceImport.value;
    }

    const bloodTypeImport = processedImportedDonorData.find((data) => data.key === 'blood_type');
    if (bloodTypeImport) {
      bloodType = bloodTypeImport.value;
    }

    const heightImport = processedImportedDonorData.find((data) => data.key === 'height_cm');
    if (heightImport) {
      const heightVal = heightImport.value ? parseFloat(heightImport.value.split(' ')[0]) : null;
      height = Number.isNaN(heightVal) ? null : heightVal;
    }

    const weightImport = processedImportedDonorData.find((data) => data.key === 'weight_kg');
    if (weightImport) {
      const weightVal = weightImport.value ? parseFloat(weightImport.value.split(' ')[0]) : null;
      weight = Number.isNaN(weightVal) ? null : weightVal;
    }

    const codImport = processedImportedDonorData.find((data) => data.key === 'cause_of_death');
    if (codImport) {
      causeOfDeath = codImport.value;
    }

    // We store type of death as either DCD or DBD
    const dcdImport = processedImportedDonorData.find((data) => data.key === 'dcd');
    if (dcdImport) {
      typeOfDeath = dcdImport.value ? (dcdImport.value.toLowerCase() === 'yes' ? 'DCD' : 'BD') : 'Unknown';
    }
  }

  // Record that the UNOS ID came from a document rather than being typed in, so
  // Update Donor can later pin its UNOS search to this donor.
  const donor_source: DonorSource = isUnosImport ? 'unos' : donorNetPdf ? 'pdf' : existingDonorId ? 'user' : 'none';

  const { result, error } = yield* typedCall(apiPost<ApiCreateCaseResponse>, api.txp.createCase, undefined, {
    opo_donor_id: opoDonorId,
    // Local vs Import donor type is no longer used in the client
    // and we default to 'Local'
    donor_type: 'Local',
    current_location: hospital,
    sex: sex || null,
    date_of_birth: dateOfBirth !== '' ? formatDate(dateOfBirth) : null,
    unos_id: unosId,
    organs,
    workflow: workflowKey,
    high_risk: highRisk,
    tags: tags.map((t) => t.tagId),
    donor_data: existingDonorId
      ? (importedDonorData || []).filter((entry) => entry.key === 'match_number')
      : processedImportedDonorData,
    donor_hospital: hospital,
    time_of_death: timeOfDeathString || null,
    race: race || null,
    blood_type: bloodType || null,
    height: height || null,
    weight: weight || null,
    cause_of_death: causeOfDeath || null,
    type_of_death: typeOfDeath || null,
    stage: stage || null,
    existing_donor_id: existingDonorId || null,
    provider_services: selectedProviderServices || [],
    donor_source: donor_source,
  });

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode && error.statusCode === 409) {
        const errorMessage = 'A case with this Ref. ID has already been created';
        yield put(
          createDonorFailed({
            nonFieldErrors: errorMessage,
          }),
        );
        yield put(setSagaMessage('', `${errorMessage}`, ''));
      } else if (error.isNetworkError) {
        const errorMessage = 'Creating case failed, are you online?';
        yield put(setSagaMessage('', `${errorMessage}`, ''));
      } else {
        const errorMessage = 'Creating case failed, try again later';
        yield put(pushError(errorMessage, error));
        yield put(setSagaMessage('', `${errorMessage}`, ''));
      }
    } else {
      const errorMessage = 'Creating case failed, try again later';
      yield put(pushError(errorMessage, error));
      yield put(setSagaMessage('', `${errorMessage}`, ''));
    }
    yield put(finishLoading('saveDonor'));
  } else {
    // As mentioned in Redmine issue 5558, as a way to ensure PHI is shared with the necessary people,
    // we removed attaching the PDF file that used to be performed here. This can return again if we
    // introduce a more concrete permissions structure
    const donorResult = result.case;
    const donorId = donorResult.donor_id;
    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors);

    const organsArray = [];
    for (let i = 0; i < donorResult.organs.length; i += 1) {
      organsArray.push({
        organId: donorResult.organs[i].organ_id,
        organType: donorResult.organs[i].organ_type,
      });
    }

    currentDonors[donorId] = {
      donorId,
      orgId: donorResult.organization_id,
      opoDonorId: donorResult.opo_donor_id,
      highRisk: donorResult.high_risk,
      tags: donorResult.tags ? donorResult.tags.map((t) => convertToTag(t)) : [],
      unosId: donorResult.unos_id,
      userId: donorResult.user_id,
      createDate: donorResult.create_date,
      lastModified: donorResult.last_modified,
      lastModifiedBy: donorResult.last_modified_by,
      closed: false,
      organs: organsArray,
      workflow: workflowKey,
      unread: donorResult.unread ?? 0,
      files: {},
      fileIds: [],
      pendingFiles: {},
      xmatFiles: {},
      notes: donorResult.notes ? donorResult.notes.map((note) => convertToCaseNote(note)) : [],
      isAdmin: donorResult.is_admin,
      rights: donorResult.rights,
      hasNewServiceAlert: donorResult.has_new_service_alert || null,
      rank: donorResult.rank,
      sequenceNumber: donorResult.sequence_number,
      stage: donorResult.stage,
      currentLocation: donorResult.current_location ?? '',
      initialClose: null,
      donorAttachments: donorResult.donor_attachments,
      caseAdmins: donorResult.case_admins,
    };

    yield put(receiveDonors(currentDonors));
    yield put(resetDonorEditState());

    // NOTE: I'm just using the saga to avoid timing/race issues
    // This should wait to complete before setting createDonorSuccess
    yield loadCasePreferencesSaga({
      type: 'Donor/LOAD_CASE_PREFERENCES',
      workflow: workflowKey,
      caseId: donorId,
    });

    yield put(finishLoading('saveDonor'));
    yield put(createDonorSuccess(donorId));
    const casePreferences = yield* typedSelect((state: RootState) => state.donor.casePreferences);
    if (casePreferences.length > 0) {
      yield router.navigate({ to: '/cases/$caseId/preferences', params: { caseId: donorId } });
    } else {
      yield router.navigate({ to: '/cases/$caseId', params: { caseId: donorId }, search: { sidebar: 'case' } });
    }
  }
}

export function* importDonorDataSaga(action: ImportDonorData): SagaIterator<void> {
  const { caseId, importedDonorData, existingDonorId, donorSource } = action;
  // Debounce double-clicks
  yield delay(50);

  yield put(startLoading('importDonorData'));

  let processedImportedDonorData = importedDonorData;

  // Preprocess date fields using timezone/DST info from the imported data,
  // mirroring the same logic used in createDonorSaga.
  if (importedDonorData && importedDonorData.length && !existingDonorId) {
    const timezoneImport = importedDonorData.find((data) => data.key === 'time_zone');
    const dstImport = importedDonorData.find((data) => data.key === 'dst_observed');

    if (timezoneImport && dstImport) {
      const tz = timezoneImport.value;
      const dst = dstImport.value.toLowerCase() === 'yes';

      processedImportedDonorData = importedDonorData.map((entry) => {
        if (entry.key.endsWith('_date')) {
          if (entry.key === 'or_date') {
            entry.value = entry.value.replace(/scheduled|tentative/gi, '').trim();
          }
          entry.value = entry.value ? formatDateTimeToTZ(entry.value, tz, dst) : '';
        }
        return entry;
      });
    }
  }

  const { result, error } = yield* typedCall(
    apiPost<ApiGetCaseTasksResponse>,
    api.txp.importDonorData,
    { caseId },
    {
      donor_data: processedImportedDonorData,
      ...(existingDonorId !== undefined ? { existing_donor_id: existingDonorId } : {}),
      donor_source: donorSource,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        const errorMessage = 'Updating donor data failed, try again later';
        yield put(setSagaMessage('', errorMessage, ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('', 'Updating donor data failed, are you online?', ''));
      } else {
        const errorMessage = 'Updating donor data failed, try again later';
        yield put(pushError(errorMessage, error));
        yield put(setSagaMessage('', errorMessage, ''));
      }
    } else {
      const errorMessage = 'Updating donor data failed, try again later';
      yield put(pushError(errorMessage, error));
      yield put(setSagaMessage('', errorMessage, ''));
    }
    yield put(importDonorDataFailed());
  } else {
    // Process returned task data
    const resultTasks = result.tasks || [];
    const taskData: TaskDataMap = {};
    for (let i = 0; i < resultTasks.length; i += 1) {
      const currentTask = resultTasks[i];
      taskData[currentTask.task_id] = currentTask.task_data;
    }

    yield put(receiveDonorTaskData(caseId, taskData));

    // Sync top-level donor identifiers (unosId, matchId, opo) so DonorHeader
    // reflects the imported values immediately without a page reload.
    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors);
    const currentCase = currentDonors[caseId];
    if (currentCase) {
      const newUnosId = processedImportedDonorData.find((e) => e.key === 'unos_id')?.value;
      const newMatchId = processedImportedDonorData.find((e) => e.key === 'match_number')?.value;
      const newOpo = processedImportedDonorData.find((e) => e.key === 'opo')?.value;
      if (newUnosId !== undefined || newMatchId !== undefined || newOpo !== undefined) {
        yield put(
          receiveDonors({
            [caseId]: {
              ...currentCase,
              ...(newUnosId !== undefined ? { unosId: newUnosId } : {}),
              ...(newMatchId !== undefined ? { matchId: newMatchId } : {}),
              ...(newOpo !== undefined ? { opo: newOpo } : {}),
            },
          }),
        );
      }
    }

    yield put(importDonorDataSuccess(caseId));
    yield put(setSagaMessage('Donor data updated successfully', '', ''));

    // reload this case so that any new UNOS attachments appear in the list
    yield put(getDonor(caseId));

    // Close only the Update Donor subview, preserving the open case sidebar.
    yield put(selectDonorView(DONOR_TASK_VIEW));
  }

  yield put(finishLoading('importDonorData'));
}

/**
 * @deprecated Use {@link patchUpdateCaseSaga} instead.
 */
export function* updateDonorSaga(action: UpdateDonor): SagaIterator<void> {
  const {
    donorId,
    lastModified,
    opoDonorId,
    highRisk,
    tags,
    unosId,
    currentLocation,
    dob,
    typeOfDeath,
    causeOfDeath,
    race,
    sex,
    weight,
    height,
    organs,
    stage,
    rank,
    sequenceNumber,
  } = action;

  yield put(startLoading('updateDonor'));
  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPut<ApiCase>,
    api.txp.updateDonor,
    { donorId },
    {
      last_modified: lastModified,
      opo_donor_id: opoDonorId,
      high_risk: highRisk,
      tags: tags.map((t) => t.tagId),
      unos_id: unosId,
      organs,
      current_location: currentLocation,
      date_of_birth: hasValue(dob) ? formatDate(dob) : null,
      type_of_death: typeOfDeath === '' ? null : typeOfDeath,
      cause_of_death: causeOfDeath === '' ? null : causeOfDeath,
      race: race !== '' ? race : null,
      sex: sex !== '' ? sex : null,
      height: height !== '' ? parseFloat(height) : null,
      weight: weight !== '' ? parseFloat(weight) : null,
      unet_link: '',
      stage,
      rank,
      sequence_number: sequenceNumber,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Updating case failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Updating case failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Updating case failed', 'Try again later', ''));
    }
    yield put(finishLoading('updateDonor'));
  } else {
    const closed = yield* typedSelect((state: RootState) => state.donor.donors[donorId].closed || false);
    const orgId = yield* typedSelect((state: RootState) => state.donor.donors[donorId].orgId || -1);

    const resOrgans = result.organs;

    const organArray: Organ[] = [];
    for (let i = 0; i < resOrgans.length; i += 1) {
      organArray.push({
        organId: resOrgans[i].organ_id,
        organType: resOrgans[i].organ_type,
      });
    }

    let incomingDOB = '';
    if (result && result.date_of_birth && result.date_of_birth.length > 10) {
      incomingDOB = result.date_of_birth.substring(0, 10);
    }

    const donorFiles = yield* typedSelect((state: RootState) => state.donor.donors[donorId].files || {});
    const donorFileIds = yield* typedSelect((state: RootState) => state.donor.donors[donorId].fileIds || []);
    const xmatFiles: FileMap = getXmatCaseFiles(result.xmat_case_id ?? '');

    const caseAdmins = yield* typedSelect((state: RootState) => state.donor.donors[donorId].caseAdmins);

    const workflow = yield* typedSelect((state: RootState) => state.donor.donors[donorId].workflow) || null;
    const lastModifiedTaskId = yield* typedSelect(
      (state: RootState) => state.donor.donors[donorId].lastModifiedTaskId,
    ) || null;
    const lastModifiedTaskDate = yield* typedSelect(
      (state: RootState) => state.donor.donors[donorId].lastModifiedTaskDate,
    ) || null;

    const updatedDonor: Donor = {
      donorId,
      opoDonorId: result.opo_donor_id,
      highRisk: result.high_risk,
      tags: result.tags ? result.tags.map((t) => convertToTag(t)) : [],
      notes: result.notes ? result.notes.map((note) => convertToCaseNote(note)) : [],
      unosId: result.unos_id || '',
      userId: result.user_id,
      opo: result.opo,
      matchId: result.match_id,
      orgId,
      createDate: result.create_date,
      closed,
      organs: organArray,
      currentLocation: result.current_location || '',
      dob: incomingDOB,
      race: result.race || '',
      sex: result.sex || '',
      typeOfDeath: result.type_of_death || '',
      causeOfDeath: result.cause_of_death || '',
      height: hasValue(result.height) ? result.height.toString() : '',
      weight: hasValue(result.weight) ? result.weight.toString() : '',
      unetLink: result.unet_link || '',
      files: donorFiles,
      fileIds: donorFileIds,
      xmatFiles,
      pendingFiles: {},
      lastModified: result.last_modified,
      lastModifiedBy: memberId,
      workflow,
      lastModifiedTaskId,
      lastModifiedTaskDate,
      isAdmin: true, //  only admins can update case info, meaning this flag should be true here,
      rights: result.rights,
      unread: result.unread,
      stage: result.stage,
      rank: result.rank,
      sequenceNumber: result.sequence_number,
      initialClose: result.initial_close,
      pinNumber: result.pin_number || null,
      hasNewServiceAlert: result.has_new_service_alert || null,
      alerts: result.alerts ?? [],
      xmatCaseId: result.xmat_case_id,
      perfusionDeviceSn: result.perfusion_device_sn,
      donorAttachments: result.donor_attachments,
      caseAdmins,
    };

    yield put(setSagaMessage('', 'Case updated!', ''));

    yield put(updateDonorSuccess(donorId, updatedDonor));

    yield put(finishLoading('updateDonor'));
  }
}

/**
 * Makes a network request to the PATCH update case endpoint and updates the donor
 * state with the latest values.
 */
export function* patchUpdateCaseSaga(action: PatchUpdateCase): SagaIterator<void> {
  const { caseId, lastModified, fields } = action;

  yield put(startLoading('updateDonor'));
  const memberId = yield* typedSelect((state) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPatch<ApiCase>,
    api.txp.patchUpdateCase,
    { caseId },
    {
      old_last_modified: lastModified,
      opo_donor_id: fields.opoDonorId,
      stage: fields.stage,
      rank: fields.rank,
      sequence_number: fields.sequenceNumber,
      high_risk: fields.highRisk,
      tags: fields.tags?.map((t) => t.tagId),
      xmat_case_id: fields.xmatCaseId,
      perfusion_device_sn: fields.perfusionDeviceSn,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Updating case failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Updating case failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Updating case failed', 'Try again later', ''));
    }
    yield put(finishLoading('updateDonor'));
  } else {
    const closed = yield* typedSelect((state: RootState) => state.donor.donors[caseId].closed || false);
    const orgId = yield* typedSelect((state: RootState) => state.donor.donors[caseId].orgId || -1);

    const resOrgans = result.organs;

    const organArray: Organ[] = [];
    for (let i = 0; i < resOrgans.length; i += 1) {
      organArray.push({
        organId: resOrgans[i].organ_id,
        organType: resOrgans[i].organ_type,
      });
    }

    let incomingDOB = '';
    if (result && result.date_of_birth && result.date_of_birth.length > 10) {
      incomingDOB = result.date_of_birth.substring(0, 10);
    }

    const donorFiles = yield* typedSelect((state: RootState) => state.donor.donors[caseId].files || {});
    const donorFileIds = yield* typedSelect((state: RootState) => state.donor.donors[caseId].fileIds || []);
    const xmatFiles: FileMap = getXmatCaseFiles(result.xmat_case_id ?? '');

    const workflow = yield* typedSelect((state: RootState) => state.donor.donors[caseId].workflow) || null;
    const lastModifiedTaskId = yield* typedSelect(
      (state: RootState) => state.donor.donors[caseId].lastModifiedTaskId,
    ) || null;
    const lastModifiedTaskDate = yield* typedSelect(
      (state: RootState) => state.donor.donors[caseId].lastModifiedTaskDate,
    ) || null;
    const existingPinNumber = yield* typedSelect((state: RootState) => state.donor.donors[caseId]?.pinNumber) || null;
    // Get existing caseAdmins for the case.
    // NOTE: we maybe could just create a partial Donor object to send the updateDonorSuccess action, instead
    // of pulling all the files, admin Ids, and other info that doesn't come back from the update request
    const caseAdmins = yield* typedSelect((state: RootState) => state.donor.donors[caseId]?.caseAdmins);

    const updatedDonor: Donor = {
      donorId: caseId,
      opoDonorId: result.opo_donor_id,
      highRisk: result.high_risk,
      tags: result.tags ? result.tags.map((t) => convertToTag(t)) : [],
      notes: result.notes ? result.notes.map((note) => convertToCaseNote(note)) : [],
      unosId: result.unos_id || '',
      userId: result.user_id,
      opo: result.opo,
      matchId: result.match_id,
      orgId,
      createDate: result.create_date,
      closed,
      organs: organArray,
      currentLocation: result.current_location || '',
      dob: incomingDOB,
      race: result.race || '',
      sex: result.sex || '',
      typeOfDeath: result.type_of_death || '',
      causeOfDeath: result.cause_of_death || '',
      height: hasValue(result.height) ? result.height.toString() : '',
      weight: hasValue(result.weight) ? result.weight.toString() : '',
      unetLink: result.unet_link || '',
      files: donorFiles,
      fileIds: donorFileIds,
      xmatFiles,
      pendingFiles: {},
      lastModified: result.last_modified,
      lastModifiedBy: memberId,
      workflow,
      lastModifiedTaskId,
      lastModifiedTaskDate,
      isAdmin: true, //  only admins can update case info, meaning this flag should be true here,
      rights: result.rights,
      unread: result.unread,
      stage: result.stage,
      rank: result.rank,
      sequenceNumber: result.sequence_number,
      pinNumber: existingPinNumber,
      initialClose: result.initial_close,
      hasNewServiceAlert: result.has_new_service_alert || null,
      alerts: result.alerts ?? [],
      xmatCaseId: result.xmat_case_id,
      perfusionDeviceSn: result.perfusion_device_sn,
      donorAttachments: result.donor_attachments,
      caseAdmins,
    };

    yield put(setSagaMessage('', 'Case updated!', ''));

    yield put(updateDonorSuccess(caseId, updatedDonor));

    yield put(finishLoading('updateDonor'));
  }
}

function getMandatoryFieldIds(formDef: FormDefData): Array<number> {
  const mandatoryFieldIds: number[] = [];
  if (!formDef) return mandatoryFieldIds;
  for (let i = 0; i < formDef.sections.length; i += 1) {
    const sectionDef = formDef.sections[i];
    // check each field
    for (let j = 0; j < sectionDef.fields.length; j += 1) {
      // to see if it is mandatory. if it is, add it to the list of mandatory values to check for
      if (sectionDef.fields[j].mandatory) {
        mandatoryFieldIds.push(sectionDef.fields[j].id);
      }
    }
  }
  return mandatoryFieldIds;
}

// function getPdfFields(action: ReceivePdfFields): Saga<void> {
export function* getPdfFieldsSaga(): SagaIterator<void> {
  // yield put(startLoading('ReceivePdfFields'));
  const { result, error } = yield* typedCall(apiFetch<ApiDonorImportFieldResponse>, api.txp.getPdfFields);

  // error handling
  if (error || !result) {
    yield put(setSagaMessage('', 'Receive PDF Fields failed', ''));
  } else {
    yield put(receivePdfFields(result.fields));
  }

  // yield put(finishLoading('ReceivePdfFields'));
}

function areMandatoryFieldsPresent(mandatoryFieldIds: number[], formData: FormValueMap): boolean {
  if (!formData) return false;

  for (let i = 0; i < mandatoryFieldIds.length; i += 1) {
    const mandatoryFieldId = mandatoryFieldIds[i];
    // check each field
    const thisField = formData[mandatoryFieldId];
    if (!thisField) return false;
    if (!thisField.value) return false;
    if (typeof thisField.value === 'string' && thisField.value.length === 0) return false;
  }
  return true;
}

function* isMandatoryDataMissing(donorId: number, taskId: number, fieldValueMap?: FormValueMap, formDef?: FormDefData) {
  const donorState = yield* typedSelect((state: RootState) => state.donor);
  const donor = donorState.donors[donorId];
  const workflow = donor.workflow || 'LEGACY';
  const { tasks } = donorState.workflowTasks[workflow];
  const taskIndex = tasks.findIndex((x) => x.taskId === taskId);
  const taskDescription =
    tasks.length > 0 && taskIndex !== -1 ? tasks[taskIndex].details.description : 'Could not find task';
  const tempFormId = taskIndex !== -1 ? tasks[taskIndex].formId : null;
  const formId = tempFormId || formIndexMap[taskDescription];
  // If there is no form associated with the task then just return false
  if (!formId) return false;

  const formDefRedux = yield* typedSelect((state: RootState) => state.forms.formDef);
  const thisFormDef = formDef || formDefRedux[formId];

  const thisFormData = fieldValueMap;
  let mandatoryFieldError = false;
  const mandatoryFieldIds = getMandatoryFieldIds(thisFormDef);
  if (mandatoryFieldIds.length > 0 && thisFormData) {
    if (!mandatoryFieldError && !areMandatoryFieldsPresent(mandatoryFieldIds, thisFormData)) {
      mandatoryFieldError = true;
    }
  }
  return mandatoryFieldError;
}

export function* loadCasePreferencesSaga(action: LoadCasePreferences) {
  const { workflow, caseId } = action;
  // First get all the donors
  const allDonors = yield* typedSelect((state: RootState) => state.donor.donors);
  const allDonorsArray: Donor[] = values(allDonors);
  const allTeams = yield* typedSelect((state: RootState) => state.team.teams);

  // Get the follower groups for the current case (if any)
  const currentCase = yield* typedSelect((state: RootState) => state.donor.donors[caseId]);
  const currentFollowers = currentCase ? currentCase.followerGroups || {} : {};
  const followerKeys = Object.keys(currentFollowers);
  const currentFollowerNames = followerKeys.map((key) => currentFollowers[parseInt(key, 10)].name);
  const { result } = yield* apiCachedFetch<ApiWorkflowAndForms>(
    ['workflowDefinition', workflow],
    api.txp.workflowDefinition,
    {
      key: workflow,
    },
  );
  const currentWorkflow = result?.workflow;
  const casePreferences = currentWorkflow?.case_preferences;

  if (casePreferences?.preferences?.followers?.length) {
    const preferences: FollowerEditState[] = [];
    for (let fIdx = 0; fIdx < casePreferences.preferences.followers.length; fIdx += 1) {
      const followerPref = casePreferences.preferences.followers[fIdx];
      if (!currentFollowerNames.includes(followerPref.name)) {
        // We need to build up our pending users, check defined teams
        const prefUsers: UserProfile[] = [];
        followerPref.teams.forEach((teamId: number) => {
          // Try to identify our team from state and load members from there
          const team = allTeams.find((t) => t.teamId === teamId);

          if (team) {
            team.teamMembers.forEach((tm) => {
              if (!prefUsers.some((u) => u.userId === tm.userId)) {
                prefUsers.push(tm);
              }
            });
          }
        });

        // Convert UserProfile[] to UserProfileWithSelection[] for compatibility
        const pendingUsersWithSelection = prefUsers.map((user) => ({
          ...user,
          isSelected: undefined,
          preference: undefined,
        }));

        // NOTE: For now, there is no way to define individual members to be specified in a case
        //     : follower preference, so we will ignore that at this time until we need to adjust
        // Build our FollowerEditState objects for the follower preferences
        preferences.push({
          followerGroupId: fIdx + 1,
          followerName: followerPref.name,
          canUpdate: followerPref.canEdit,
          taskIds: followerPref.tasks,
          pendingUsers: pendingUsersWithSelection,
          pendingRemovedUsers: [],
          followerHasChanged: false,
          memberPreferences: followerPref.memberPreferences ?? {},
          muteNotification: !(followerPref.notifyUponCreation ?? true),
        });
      }
    }

    yield put(setCasePreferences(preferences));
  } else {
    // The defined workflow does not have any case preference followers specified, so go
    // the old route of looking for the most recently created case of the same workflow
    // Then sort and filter the donors, to get the most recent donor for the workflow
    const sortedAndFilteredDonors = allDonorsArray
      .filter((donor) => donor.donorId !== caseId && donor.workflow === workflow)
      .sort((a, b) => compareDesc(a.createDate, b.createDate));
    if (sortedAndFilteredDonors.length > 0) {
      // NOTE: currently just looking at the last 5 cases to get the case preferences
      // Eventually we'll just get the preferences from a workflow-defined object
      const mostRecentDonors = sortedAndFilteredDonors.slice(0, 5);
      const mostRecentIds = mostRecentDonors.map((donor) => donor.donorId);
      // Make the request to get all follower info for that donor
      yield getCasePreferencesFromCases(mostRecentIds, currentFollowerNames);
    }
  }
}

function* getCasePreferencesFromCases(caseIds: number[], currentFollowerNames: string[]): SagaIterator<void> {
  // NOTE: For now, we'll just take the first case that has followers to get preferences/presets
  for (let caseIdx = 0; caseIdx < caseIds.length; caseIdx += 1) {
    const caseId = caseIds[caseIdx];
    const { result } = yield* typedCall(
      apiFetch<ApiGetCaseFollowerGroupsDetailedResponse>,
      api.txp.caseFollowersDetailed,
      { caseId },
    );
    const casePreferences: FollowerEditState[] = [];
    if (result && result.followers && result.followers.length > 0) {
      for (let followerIdx = 0; followerIdx < result.followers.length; followerIdx += 1) {
        const follower = result.followers[followerIdx];
        // Only add to the preferences if there isn't already a group with this name
        if (!currentFollowerNames.includes(follower.name)) {
          casePreferences.push({
            followerGroupId: follower.follower_id,
            followerName: follower.name,
            canUpdate: follower.can_update,
            taskIds: follower.task_ids,
            pendingUsers: follower.member_users.map((user) => parseProfile(user)),
            pendingRemovedUsers: [], // NOTE: we won't need prendingRemovedUsers for editing case preferences
            followerHasChanged: false,
          });
        }
      }

      yield put(setCasePreferences(casePreferences));
      break;
    }
  }
}

export function* updateTaskNoteSaga(action: UpdateTaskNote): SagaIterator<void> {
  const { donorId, taskId, noteContent, lastModified } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);
  yield put(startLoading('donorTask'));
  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPut<ApiCaseTaskHistory>,
    api.txp.updateNote,
    { donorId, taskId },
    {
      note_content: noteContent,
      last_modified: lastModified !== '' ? lastModified : null,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(pushError(parseResponseTextError(error.responseText)));
      } else if (error.isNetworkError) {
        yield put(pushError('Updating task note failed, are you online?', error));
      } else {
        yield put(pushError('Updating task note failed, try again later', error));
      }
    } else {
      yield put(pushError('Updating task note failed, try again later', error));
    }
  } else {
    const historyArray = [];
    for (let i = result.history.length - 1; i >= 0; i -= 1) {
      historyArray.push({
        userId: result.history[i].user_id,
        updateTime: result.history[i].update_time,
        completed: result.history[i].completed,
        notApplicable: result.history[i].not_applicable,
        noteContent: result.history[i].note_content || '',
      });
    }

    const taskData = [];
    const taskDataKeys = result.task_data ? keys(result.task_data) : [];
    for (let i = 0; i < taskDataKeys.length; i += 1) {
      taskData.push({
        fieldId: taskDataKeys[i],
        data: result.task_data[taskDataKeys[i]],
      });
    }

    const donorTask = {
      donorId: result.donor_id,
      taskId: result.task_id,
      completed: result.completed,
      notApplicable: result.not_applicable,
      noteContent: result.note_content || '',
      previousTaskId: result.previous_task_id,
      dueDateInterval: result.due_date_interval,
      dueDate: result.due_date,
      dueDateComment: result.due_date_comment,
      history: historyArray,
      lastModified: result.last_modified,
      lastModifiedBy: memberId,
    };

    const donor = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const workflow = donor.workflow || 'LEGACY';
    const allTasks = yield* typedSelect((state: RootState) => state.donor.workflowTasks[workflow].tasks);
    const taskIndex = allTasks.findIndex((x) => x.taskId === taskId);
    const taskDescription = taskIndex !== -1 ? allTasks[taskIndex].details.description : null;
    const tempFormId = taskIndex !== -1 ? allTasks[taskIndex].formId : null;
    const formId = tempFormId || formIndexMap[taskDescription || ''];

    const formUpdated = yield* typedSelect((state: RootState) => state.forms.formUpdated);

    if (hasValue(formId) && !formUpdated) {
      yield put(bulkReceiveFormData(formId, taskData));
    }

    yield put(receiveDonorTask(donorTask));

    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors || {});
    const oldTasks = yield* typedSelect((state: RootState) => state.donor.donors[donorId].tasks || []);
    const tasks = oldTasks.slice(0);

    let index = tasks.findIndex((x) => x.taskId === taskId);
    if (index === -1) {
      tasks.push({
        taskId,
        completed: false,
        notApplicable: false,
        hasNoteContent: true,
        lastModified: result.last_modified,
        lastModifiedBy: result.last_modified_by,
        hasTaskData: Boolean(result.task_data),
      });
      index = tasks.length - 1;
    } else {
      tasks[index].hasNoteContent = true;
      tasks[index].lastModified = result.last_modified;
    }

    currentDonors[donorId] = {
      ...currentDonors[donorId],

      tasks,
    };

    yield put(receiveDonors(currentDonors));
  }
  yield put(finishLoading('donorTask'));
}

export function* updateTaskStatusSaga(action: UpdateTaskStatus) {
  const { donorId, taskId, lastModified, fieldValueMap, completed, notApplicable, fromList, formDef } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  // On task completion only, check for mandatory fields. Setting n/a or incomplete do not need to do this
  if (completed) {
    const mandatoryFieldError = yield* isMandatoryDataMissing(donorId, taskId, fieldValueMap, formDef);
    if (mandatoryFieldError) {
      yield put(
        setSagaMessage(
          '',
          'Cannot mark this task as complete without first completing all mandatory fields',
          'OK',
          true,
        ),
      );
      return;
    }
  }

  const alreadyUpdating = yield* typedSelect((state: RootState) => state.loading.taskStatus[`${taskId}`]);
  if (alreadyUpdating) {
    return;
  }

  const payload = {
    completed,
    not_applicable: notApplicable,
    last_modified: lastModified !== '' ? lastModified : null, // Send in null for tasks that have never been modified
    task_data: fieldValueMap,
  };

  // don't send in task_data at all if not provided (most tasks don't have a form)
  if (!fieldValueMap) delete payload.task_data;

  yield put(startLoadingEntity('taskStatus', taskId));
  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPut<ApiCaseTaskHistory>,
    api.txp.updateStatus,
    { donorId, taskId },
    payload,
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Updating task status failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Updating task status failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Updating task status failed', 'Try again later', ''));
    }
    yield put(finishLoadingEntity('taskStatus', taskId));
  } else {
    if (!fromList) {
      const historyArray = [];
      for (let i = result.history.length - 1; i >= 0; i -= 1) {
        historyArray.push({
          userId: result.history[i].user_id,
          updateTime: result.history[i].update_time,
          completed: result.history[i].completed,
          notApplicable: result.history[i].not_applicable,
          noteContent: result.history[i].note_content || '',
        });
      }

      const taskData = [];
      const taskDataKeys = result.task_data ? keys(result.task_data) : [];
      for (let i = 0; i < taskDataKeys.length; i += 1) {
        taskData.push({
          fieldId: taskDataKeys[i],
          data: result.task_data[taskDataKeys[i]],
        });
      }

      const donorTask = {
        donorId: result.donor_id,
        taskId: result.task_id,
        completed: result.completed,
        notApplicable: result.not_applicable,
        noteContent: result.note_content || '',
        previousTaskId: result.previous_task_id,
        dueDateInterval: result.due_date_interval,
        dueDate: result.due_date,
        dueDateComment: result.due_date_comment,
        history: historyArray,
        lastModified: result.last_modified,
        lastModifiedBy: memberId,
      };

      const donor = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
      const workflow = donor.workflow || 'LEGACY';
      const tasks = yield* typedSelect((state: RootState) => state.donor.workflowTasks[workflow].tasks);
      const index = tasks.findIndex((x) => x.taskId === taskId);
      const taskDescription = index !== -1 ? tasks[index].details.description : '';
      const tempFormId = index !== -1 ? tasks[index].formId : null;
      const formId = tempFormId || formIndexMap[taskDescription];

      if (hasValue(formId) && fieldValueMap) {
        yield put(bulkReceiveFormData(formId, taskData));
      }

      yield put(receiveDonorTask(donorTask));
    }

    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors || {});
    const oldTasks = yield* typedSelect((state: RootState) => state.donor.donors[donorId].tasks || []);
    const tasks = oldTasks.slice();

    const index = tasks.findIndex((x) => x.taskId === taskId);
    if (index !== -1) {
      tasks[index].completed = result.completed;
      tasks[index].notApplicable = result.not_applicable;
      tasks[index].lastModified = result.last_modified;
      tasks[index].hasTaskData = result.task_data ? keys(result.task_data).length > 0 : false;
    } else {
      tasks.push({
        taskId,
        completed: result.completed,
        notApplicable: result.not_applicable,
        hasNoteContent: false,
        hasTaskData: fieldValueMap !== null,
        lastModified: result.last_modified,
        lastModifiedBy: memberId,
      });
    }

    if (completed) {
      currentDonors[donorId] = {
        ...currentDonors[donorId],

        lastModifiedTaskId: taskId,
        lastModifiedTaskDate: isoNowUTC(),
        tasks,
      };
    } else {
      currentDonors[donorId] = {
        ...currentDonors[donorId],

        tasks,
      };
    }

    yield put(receiveDonors(currentDonors));
    // NOTE: retrieve all data for this donor after updating the status, in case other task
    // due dates depend on this task that has been updated
    yield retrieveSingleDonorData(donorId);
    yield put(getDonorTaskData(donorId));

    let toastMessage = 'Status updated!';

    if (notApplicable !== null) {
      if (notApplicable) {
        toastMessage = 'Marked as not applicable!';
      } else {
        toastMessage = 'Marked as applicable!';
      }
    } else if (completed !== null) {
      if (completed) {
        toastMessage = 'Marked as complete!';
      } else {
        toastMessage = 'Marked as incomplete!';
      }
    }

    yield put(finishLoadingEntity('taskStatus', taskId));

    yield put(setSagaMessage('', toastMessage, ''));
  }
}

export function* updateTaskDueDateSaga(action: UpdateTaskDueDate): SagaIterator<void> {
  const { donorId, taskId, previousTaskId, interval, lastModified, dueDateComment } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);
  yield put(startLoading('donorTask'));

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPut<ApiCaseTaskHistory>,
    api.txp.updateDonorTaskDueDate,
    { donorId, taskId },
    {
      previous_task_id: previousTaskId,
      due_date_interval: interval,
      last_modified: lastModified !== '' ? lastModified : null,
      due_date_comment: dueDateComment || null,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(pushError(parseResponseTextError(error.responseText)));
      } else if (error.isNetworkError) {
        yield put(pushError('Updating form failed, are you online?', error));
      } else {
        yield put(pushError('Updating form failed, try again later', error));
      }
    } else {
      yield put(pushError('Updating form failed, try again later', error));
    }
  } else {
    const historyArray = [];
    for (let i = result.history.length - 1; i >= 0; i -= 1) {
      historyArray.push({
        userId: result.history[i].user_id,
        updateTime: result.history[i].update_time,
        completed: result.history[i].completed,
        notApplicable: result.history[i].not_applicable,
        noteContent: result.history[i].note_content || '',
      });
    }

    const taskData = [];
    const taskDataKeys = result.task_data ? keys(result.task_data) : [];
    for (let i = 0; i < taskDataKeys.length; i += 1) {
      taskData.push({
        fieldId: taskDataKeys[i],
        data: result.task_data[taskDataKeys[i]],
      });
    }

    const donorTask = {
      donorId: result.donor_id,
      taskId: result.task_id,
      completed: result.completed,
      notApplicable: result.not_applicable,
      noteContent: result.note_content || '',
      previousTaskId: result.previous_task_id,
      dueDateInterval: result.due_date_interval,
      dueDate: result.due_date,
      dueDateComment: result.due_date_comment,
      history: historyArray,
      lastModified: result.last_modified,
      lastModifiedBy: memberId,
    };

    const donor = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const workflow = donor.workflow || 'LEGACY';
    const allTasks = yield* typedSelect((state: RootState) => state.donor.workflowTasks[workflow].tasks);
    const taskIndex = allTasks.findIndex((x) => x.taskId === taskId);
    const taskDescription = taskIndex !== -1 ? allTasks[taskIndex].details.description : '';
    const tempFormId = taskIndex !== -1 ? allTasks[taskIndex].formId : null;
    const formId = tempFormId || formIndexMap[taskDescription];

    if (hasValue(formId)) {
      yield put(bulkReceiveFormData(formId, taskData));
    }

    yield put(receiveDonorTask(donorTask));

    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors || {});
    const oldTasks = yield* typedSelect((state: RootState) => state.donor.donors[donorId].tasks || []);
    const tasks = oldTasks.slice(0);

    let index = tasks.findIndex((x) => x.taskId === taskId);
    if (index === -1) {
      tasks.push({
        taskId,
        completed: false,
        notApplicable: false,
        hasNoteContent: false,
        hasTaskData: true,
        lastModified: result.last_modified,
        lastModifiedBy: result.last_modified_by,
        previousTaskId: result.previous_task_id,
        dueDate: result.due_date,
        dueDateInterval: result.due_date_interval,
        dueDateComment: result.due_date_comment,
      });
      index = tasks.length - 1;
    } else {
      tasks[index].lastModified = result.last_modified;
      tasks[index].previousTaskId = result.previous_task_id;
      tasks[index].dueDateInterval = result.due_date_interval;
      tasks[index].dueDate = result.due_date;
      tasks[index].dueDateComment = result.due_date_comment;
    }

    currentDonors[donorId] = {
      ...currentDonors[donorId],

      tasks,
    };

    yield put(getDonorTaskData(donorId));

    yield put(receiveDonors(currentDonors));
  }

  yield put(finishLoading('donorTask'));
}

export function* updateDonorTaskDataSaga(action: UpdateDonorForm): SagaIterator<void> {
  const { donorId, taskId, formValueMap, lastModified, notify, notifyFullTask, notifyAllTasks } = action;

  yield put(startLoading('taskSaving'));

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const { result, error } = yield* typedCall(
    apiPut<ApiCaseTaskHistory>,
    api.txp.updateDonorTaskData,
    {
      donorId,
      taskId,
    },
    {
      task_data: formValueMap,
      last_modified: lastModified !== '' ? lastModified : null,
      notify,
      send_full_task: notifyFullTask,
      notify_all_tasks: notifyAllTasks,
    },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        const errorMessage = parseResponseTextError(error.responseText);
        yield put(pushError(errorMessage));
        yield put(setSagaMessage('Update Error', `${errorMessage}`, ''));
      } else if (error.isNetworkError) {
        yield put(pushError('Updating form failed, are you online?', error));
        yield put(setSagaMessage('', 'Updating form failed, are you online?', ''));
      } else {
        yield put(pushError('Updating form failed, try again later', error));
        yield put(setSagaMessage('', 'Updating form failed, try again later', ''));
      }
    } else {
      yield put(pushError('Updating form failed, try again later', error));
      yield put(setSagaMessage('', 'Updating form failed, try again later', ''));
    }
  } else {
    const historyArray = [];
    for (let i = result.history.length - 1; i >= 0; i -= 1) {
      historyArray.push({
        userId: result.history[i].user_id,
        updateTime: result.history[i].update_time,
        completed: result.history[i].completed,
        notApplicable: result.history[i].not_applicable,
        noteContent: result.history[i].note_content || '',
      });
    }

    const taskData = [];
    const taskDataKeys = result.task_data ? keys(result.task_data) : [];
    for (let i = 0; i < taskDataKeys.length; i += 1) {
      taskData.push({
        fieldId: taskDataKeys[i],
        data: result.task_data[taskDataKeys[i]],
      });
    }

    const donorTask = {
      donorId: result.donor_id,
      taskId: result.task_id,
      completed: result.completed,
      notApplicable: result.not_applicable,
      noteContent: result.note_content || '',
      previousTaskId: result.previous_task_id,
      dueDateInterval: result.due_date_interval,
      dueDate: result.due_date,
      dueDateComment: result.due_date_comment,
      history: historyArray,
      lastModified: result.last_modified,
      lastModifiedBy: memberId,
    };

    const donor = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const workflow = donor.workflow || 'LEGACY';
    const allTasks = yield* typedSelect((state: RootState) => state.donor.workflowTasks[workflow].tasks);
    const taskIndex = allTasks.findIndex((x) => x.taskId === taskId);
    const taskDescription = taskIndex !== -1 ? allTasks[taskIndex].details.description : '';
    const tempFormId = taskIndex !== -1 ? allTasks[taskIndex].formId : null;
    const formId = tempFormId || formIndexMap[taskDescription];

    if (hasValue(formId)) {
      yield put(bulkReceiveFormData(formId, taskData));
    }

    yield put(receiveDonorTask(donorTask));

    const currentDonors = yield* typedSelect((state: RootState) => state.donor.donors || {});
    const oldTasks = yield* typedSelect((state: RootState) => state.donor.donors[donorId].tasks || []);
    const tasks = oldTasks.slice(0);

    let index = tasks.findIndex((x) => x.taskId === taskId);
    if (index === -1) {
      tasks.push({
        taskId,
        completed: false,
        notApplicable: false,
        hasNoteContent: false,
        hasTaskData: true,
        lastModified: result.last_modified,
        lastModifiedBy: result.last_modified_by,
      });
      index = tasks.length - 1;
    } else {
      tasks[index].lastModified = result.last_modified;
    }

    currentDonors[donorId] = {
      ...currentDonors[donorId],

      tasks,
    };

    // After updating the task, clear all dirty/edit-lock state
    yield put(formChanged(false));
    yield put(donorChanged(false));
    yield put(setEditCaseTaskStatus(donorId, taskId, undefined));

    yield put(getDonorTaskData(donorId));

    yield put(receiveDonors(currentDonors));
  }

  yield put(finishLoading('taskSaving'));
}

export function* loadDonorFilesSaga(action: LoadDonorFiles): SagaIterator<void> {
  const { donorId } = action;

  const resArr: AppFile[] = [];

  yield put(startLoadingEntity('donorFiles', donorId));

  const { result, error } = yield* typedCall(apiFetch<ApiGetCaseFilesResponse>, api.txp.donorFiles, {
    donorId,
  });

  if (error || !result || !result.media_files || !Array.isArray(result.media_files)) {
    yield put(pushError('Loading case files failed, try again later', error));
    return;
  }

  result.media_files.forEach((fileData) => {
    let messageType: MessageType | undefined;
    switch (fileData.mime_type) {
      case 'image/jpeg':
        messageType = MessageTypes.jpeg;
        break;

      case 'image/jpg':
        messageType = MessageTypes.jpg;
        break;

      case 'image/png':
        messageType = MessageTypes.png;
        break;

      case 'image/gif':
        messageType = MessageTypes.gif;
        break;

      case 'video/mp4':
        messageType = MessageTypes.mp4;
        break;

      case 'video/quicktime':
        messageType = MessageTypes.mov;
        break;

      case 'application/pdf':
        messageType = MessageTypes.pdf;
        break;

      case 'application/msword':
        messageType = MessageTypes.doc;
        break;

      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        messageType = MessageTypes.docx;
        break;

      case 'text/csv':
        messageType = MessageTypes.csv;
        break;

      case 'application/vnd.ms-excel':
        messageType = MessageTypes.xls;
        break;

      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        messageType = MessageTypes.xlsx;
        break;

      default:
        // if the file is unknown mime the upload will fail anyway
        //  so we also set the message type to 0 to ensure message sending fails as well
        messageType = MessageTypes.unknown;
    }

    resArr.push({
      id: fileData.media_file_id,
      fileName: fileData.file_path,
      displayName: fileData.display_name || fileData.file_path,
      mime: fileData.mime_type,
      memberId: fileData.user_id,
      sentTime: fileData.upload_date,
      size: fileData.file_size,
      messageType,
      presignedUrl: fileData.presigned_url,
      presignedThumbnailUrl: fileData.presigned_thumbnail_url,
    });
  });

  const files: FileMap = {};
  const fileIds: MessageId[] = [];

  (resArr || []).forEach((file) => {
    files[`${file.id}`] = file;
    fileIds.push(file.id);
  });

  yield put(receiveDonorFiles(donorId, files, fileIds));

  yield put(finishLoadingEntity('donorFiles', donorId));
}

export function* uploadDonorFileSaga(action: UploadDonorFile) {
  const { donorId, attachments, isCaseAttachment } = action;

  if (attachments && attachments.length > 0) {
    yield put(startLoading('media'));
    const token = yield* typedSelect((state: RootState) => state.auth.accessToken);
    const apiRoot = (import.meta.env.VITE_API_ROOT || '').replace(/\/$/, '');
    const uploadErrors = [];
    for (let i = 0; i < attachments.length; i += 1) {
      try {
        const encodedName = encodeURIComponent(attachments[i].fileName);
        const encodedMime = encodeURIComponent(attachments[i].mime);
        const apiPath = `${apiRoot}/donor/${donorId}/file/${encodedName}/${encodedMime}`;
        const form = new FormData();
        form.append('media_file', attachments[i].file);

        const headers = {
          Apikey: import.meta.env.VITE_API_KEY || '',
          Authorization: `Bearer ${token}`,
        };

        const options = {
          method: 'POST',
          headers,
          body: form,
        };

        const result: ApiDonorFileUploadResponse = yield fetch(apiPath, options).then((response) => response.json());

        if (result.error) {
          uploadErrors.push(`Failed to upload file: ${attachments[i].fileName}`);
        }
      } catch (error) {
        const newError = error instanceof Error ? error : new Error('Unknown upload error');
        yield put(pushError('Failed to upload media', newError));
        yield put(uploadDonorFileFailed('failed'));
      }
    }

    if (uploadErrors.length === 1) {
      const message =
        uploadErrors[0] + (isCaseAttachment ? '. You can attach the PDF to this case in the Files tab.' : '');
      yield put(setSagaMessage('', message, ''));
    } else if (uploadErrors.length > 1) {
      yield put(setSagaMessage('', `Failed to upload ${uploadErrors.length} files`, ''));
    } else {
      yield put(uploadDonorFileSuccess());
    }

    if (attachments.length > uploadErrors.length) {
      // reload files for this donor so the new ones appear in the list
      yield put(loadDonorFiles(donorId));
    }

    yield put(finishLoading('media'));
  }
}

export function* deleteDonorFileSaga(action: DeleteDonorFile) {
  const { mediaFileId, donorId } = action;

  yield put(startLoading('media'));
  const token = yield* typedSelect((state: RootState) => state.auth.accessToken);
  const apiRoot = (import.meta.env.VITE_API_ROOT || '').replace(/\/$/, '');
  const uploadErrors = [];

  try {
    const apiPath = `${apiRoot}/media/${mediaFileId}/delete`;

    const headers = {
      Apikey: import.meta.env.VITE_API_KEY || '',
      Authorization: `Bearer ${token}`,
    };

    const options = {
      method: 'PUT',
      headers,
    };

    const result: ApiDeleteDonorFileResponse = yield fetch(apiPath, options);
    if (result.error) {
      uploadErrors.push(`Failed to delete file: ${mediaFileId}`);
    }
  } catch (error) {
    const newError = error instanceof Error ? error : new Error('Unknown error');
    yield put(pushError('Failed to delete media', newError));
    yield put(deleteDonorFileFailed('failed'));
  }
  if (uploadErrors.length === 1) {
    yield put(setSagaMessage('', uploadErrors[0], ''));
  } else {
    yield put(deleteDonorFileSuccess());
  }

  yield put(finishLoading('media'));
  // now reload files for this donor
  yield put(loadDonorFiles(donorId));
}

export function* getDonorTaskSaga(action: GetDonorTask): SagaIterator<void> {
  const { donorId, taskId } = action;

  yield put(startLoading('donorTask'));

  const { result: resultRaw, error } = yield* typedCall(apiFetch<ApiCaseTaskHistory>, api.txp.donorTask, {
    donorId,
    taskId,
  });

  let result = resultRaw;
  if ((error && error instanceof InvalidResponseCode && error.statusCode === 404) || !result) {
    // if not found, set default values
    result = {
      task_data: {},
      donor_id: donorId,
      task_id: taskId,
      completed: false,
      not_applicable: false,
      note_content: '',
      previous_task_id: undefined,
      due_date_interval: undefined,
      due_date: undefined,
      due_date_comment: undefined,
      history: [],
      last_modified: '',
      last_modified_by: 0,
      edit_by: undefined,
      edit_time: undefined,
    };
  } else if (error) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Loading task failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Loading task failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Loading task failed', 'Try again later', ''));
    }
    yield put(finishLoading('donorTask'));
    return;
  }
  const historyArray = [];
  for (let i = result.history.length - 1; i >= 0; i -= 1) {
    historyArray.push({
      userId: result.history[i].user_id,
      updateTime: result.history[i].update_time,
      completed: result.history[i].completed,
      notApplicable: result.history[i].not_applicable,
      noteContent: result.history[i].note_content || '',
    });
  }

  const taskData: TaskDataArrayItem[] = [];
  const donor = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
  const workflow = donor?.workflow || 'LEGACY';
  const tasks = yield* typedSelect((state: RootState) => state.donor.workflowTasks[workflow]?.tasks || []);
  const index = tasks.findIndex((x) => x.taskId === taskId);
  const taskDescription = index !== -1 ? tasks[index].details.description : '';
  const tempFormId = index !== -1 ? tasks[index].formId : null;
  const formId = tempFormId || formIndexMap[taskDescription];

  if (hasValue(formId)) {
    if (result.task_data) {
      const taskDataKeys = Object.keys(result.task_data);
      taskDataKeys.forEach((key) => {
        const data = result.task_data[key];
        taskData.push({
          fieldId: key,
          data,
        });
      });
    }
  }

  const donorTask = {
    donorId: result.donor_id,
    taskId: result.task_id,
    completed: result.completed,
    notApplicable: result.not_applicable,
    noteContent: result.note_content || '',
    previousTaskId: result.previous_task_id,
    dueDateInterval: result.due_date_interval,
    dueDate: result.due_date,
    dueDateComment: result.due_date_comment,
    history: historyArray,
    lastModified: result.last_modified,
    lastModifiedBy: result.last_modified_by,
    editBy: result.edit_by,
    editTime: result.edit_time,
  };

  const formUpdated = yield* typedSelect((state: RootState) => state.forms.formUpdated);

  if (hasValue(formId) && !formUpdated) {
    yield put(setFormErrors(formId, {}));
    yield put(bulkReceiveFormData(formId, taskData));
  }

  yield put(receiveDonorTask(donorTask));
  yield put(receiveEditCaseTaskStatus(donorTask.donorId, donorTask.taskId, donorTask.editBy, donorTask.editTime));
  yield put(finishLoading('donorTask'));
}

export function* getDonorTaskDataSaga(action: GetDonorTaskData): SagaIterator<void> {
  const { donorId } = action;

  yield put(startLoading('taskData'));

  const { result, error } = yield* typedCall(apiFetch<ApiGetCaseTasksResponse>, api.txp.donorTasks, {
    donorId,
  });

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error instanceof InvalidResponseCode) {
        yield put(setSagaMessage('', parseResponseTextError(error.responseText), ''));
      } else if (error.isNetworkError) {
        yield put(setSagaMessage('Loading tasks failed', 'Are you online?', ''));
      } else {
        yield put(setSagaMessage('Loading tasks failed', 'Try again later', ''));
      }
    } else {
      yield put(setSagaMessage('Loading tasks failed', 'Try again later', ''));
    }
    yield put(finishLoading('taskData'));
  } else {
    const resultTasks = result.tasks || [];
    const taskData: TaskDataMap = {};
    for (let i = 0; i < resultTasks.length; i += 1) {
      const currentTask = resultTasks[i];
      taskData[currentTask.task_id] = currentTask.task_data;
    }

    yield put(receiveDonorTaskData(donorId, taskData));
    yield put(finishLoading('taskData'));
  }
}

export function* loadOrgMembersSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);
  yield put(startLoading('orgMembers'));

  const organizationId = yield* typedSelect((state: RootState) => selectProfileOrganizationId(state.auth));

  const { result, error } = yield* typedCall(
    apiFetch<ApiGetOrganizationMemberResponse>,
    api.txp.getOrganizationMembers,
    {
      orgId: organizationId,
    },
  );

  if (error || !result) {
    yield put(pushError('Failed to get organization members', error));
    yield put(finishLoading('orgMembers'));
  } else {
    const members = [];
    const memberArray = result.organization_members;

    for (let i = 0; i < memberArray.length; i += 1) {
      members.push(parseProfile(memberArray[i].user_information));
    }

    yield put(receiveOrganizationMembers(members));
    yield put(finishLoading('orgMembers'));
  }
}

const parsePartialFollower = (followerRes: ApiPartialFollowerGroup): FollowerGroup => {
  const follower: FollowerGroup = {
    followerGroupId: followerRes.follower_id,
    chatroomId: followerRes.chatroom_id,
    memberUsers: [],
    externalUsers: [],
    invitedUsers: [],
    name: followerRes.name,
    tasks: [],
  };

  if (followerRes.external_users) {
    for (let i = 0; i < followerRes.external_users.length; i += 1) {
      follower.externalUsers.push(parseExternalUser(followerRes.external_users[i]));
    }
  }

  if (followerRes.member_users) {
    for (let i = 0; i < followerRes.member_users.length; i += 1) {
      follower.memberUsers.push(parseProfile(followerRes.member_users[i]));
    }
  }

  if (followerRes.invited_users) {
    for (let i = 0; i < followerRes.invited_users.length; i += 1) {
      follower.invitedUsers.push(parseFollowerInvite(followerRes.invited_users[i]));
    }
  }

  return follower;
};

export function* getDonorFollowerGroupsSaga(action: GetDonorFollowerGroups): SagaIterator<void> {
  const { donorId } = action;

  const { result, error } = yield* typedCall(apiFetch<ApiGetCaseFollowerGroupsResponse>, api.txp.donorFollowers, {
    donorId,
  });

  if (error || !result) {
    yield put(pushError('Failed to get the follower group list', error));
  } else {
    const followers = result.followers || [];
    const followersMap: FollowerGroupMap = {};
    for (let i = 0; i < followers.length; i += 1) {
      followersMap[followers[i].follower_id] = parsePartialFollower(followers[i]);
    }

    yield put(receiveDonorFollowerGroups(donorId, followersMap));
  }
}

export function* getFollowerGroupSaga(action: GetFollowerGroup): SagaIterator<void> {
  const { donorId, followerGroupId, initEditState } = action;

  yield put(startLoading('follower'));

  const { result, error } = yield* typedCall(apiFetch<ApiFollowerDetails>, api.txp.follower, {
    followerId: followerGroupId,
  });

  if (error || !result) {
    yield put(pushError('Failed to get follower group', error));
    yield put(setSagaMessage('', 'Error loading follower data', ''));
  } else {
    const workflowCase = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);

    if (!workflowCase) {
      yield put(setSagaMessage('', 'Case data not available. Please reload the case and try again.', ''));
      yield put(finishLoading('follower'));
      return;
    }

    const donorFollowerGroups = workflowCase.followerGroups ? workflowCase.followerGroups : {};
    const followerGroup: FollowerGroup = {
      followerGroupId: result.follower.follower_id,
      chatroomId: result.follower.chatroom_id,
      name: result.follower.name,
      tasks: result.task_ids,
      memberUsers: result.member_users ? result.member_users.map((user) => parseProfile(user)) : [],
      externalUsers: result.external_users ? result.external_users.map((user) => parseExternalUser(user)) : [],
      invitedUsers: result.invited_users ? result.invited_users.map((invite) => parseFollowerInvite(invite)) : [],
      canUpdate: result.follower.can_update,
    };
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        ...followerGroup,
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
    // If the follower group was loaded for editing, initialize the edit state with the loaded data
    if (initEditState) {
      yield put(initFollowerEditData(followerGroup));
    }
  }

  yield put(finishLoading('follower'));
}

export function* createFollowerGroupSaga(action: CreateFollowerGroup) {
  const { donorId, followerGroup } = action;

  yield put(startLoading('follower'));

  const result = yield* createFollowerGroupGenerator(donorId, followerGroup);

  if (!result.groupCreationError) {
    // Follower group creation was successful - Reset follower edit state
    yield put(resetFollowerEditData());
    yield put(openFollowerGroup(result.followerGroupId));
    yield put(setSagaMessage('', `${result.followerName} created!`, ''));
  } else {
    yield put(setSagaMessage('', `Failed to create a case follower: ${result.followerName}`, ''));
  }

  // After updating everything, change the donor view the general status view
  // NOTE: we need to wait for the above to finish before navigating, because swithcing the donor
  // view will reload follower groups and could overwrite edited information
  yield put(selectDonorView(DONOR_STATUS_VIEW));
  yield put(finishLoading('follower'));
}

/**
 * This saga creates multiple follower groups in parallel. The creation results are returned from a helper
 * generator function and interpreted to determine what saga message should be set.
 */
export function* createMultipleFollowerGroupsSaga(action: CreateMultipleFollowerGroups): SagaIterator<void> {
  const { donorId, followerGroups } = action;

  yield put(startLoading('follower'));

  const results = yield* typedAll(followerGroups.map((f) => call(createFollowerGroupGenerator, donorId, f)));

  if (results.some((r) => r.groupCreationError)) {
    const failedFollowerGroupNames = results
      .filter((r) => r.groupCreationError)
      .map((r) => r.followerName)
      .join(', ');
    yield put(setSagaMessage('', `Failed to create follower group(s): ${failedFollowerGroupNames}`, ''));
  } else if (results.some((r) => r.memberUpdateError)) {
    const failedFollowerGroupNames = results
      .filter((r) => r.memberUpdateError)
      .map((r) => r.followerName)
      .join(', ');
    yield put(
      setSagaMessage(
        '',
        `An error occurred while adding members to these follower groups: ${failedFollowerGroupNames}`,
        '',
        false,
        true,
      ),
    );
  } else {
    // No errors, all follower groups created successfully
    const createdFollowerGroupNames = results.map((r) => r.followerName).join(', ');
    yield put(setSagaMessage('', `Created follower group(s): ${createdFollowerGroupNames}!`, '', false, true));
  }

  yield put(finishLoading('follower'));
}

type CreateFollowerGroupResult = {
  groupCreationError: boolean;
  memberUpdateError: boolean;
  followerName: string;
  followerGroupId?: number;
};

/**
 * Helper generator function for creating a follower group.
 *
 * @returns follower group Id if successful, null if unsuccessful
 */
function* createFollowerGroupGenerator(
  donorId: number,
  followerGroup: FollowerEditState,
): Generator<StrictEffect, CreateFollowerGroupResult> {
  const { result, error } = yield* typedCall(apiPost<ApiFollowerDetails>, api.txp.createFollowerGroup, undefined, {
    donor_id: donorId,
    name: followerGroup.followerName,
  });

  const createResult: CreateFollowerGroupResult = {
    groupCreationError: false,
    memberUpdateError: false,
    followerName: followerGroup.followerName,
    followerGroupId: undefined,
  };

  if (error || !result) {
    createResult.groupCreationError = true;
    yield put(pushError('Failed to create the follower group', error));
  } else {
    const followerGroupId = result.follower.follower_id;
    const donorState = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};

    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        followerGroupId,
        chatroomId: result.follower.chatroom_id,
        name: result.follower.name,
        tasks: result.task_ids,
        memberUsers: result.member_users ? result.member_users.map((user) => parseProfile(user)) : [],
        externalUsers: result.external_users ? result.external_users.map((user) => parseExternalUser(user)) : [],
        invitedUsers: result.invited_users ? result.invited_users.map((invite) => parseFollowerInvite(invite)) : [],
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
    const { pendingUsers } = followerGroup;
    const memberUsers = result.member_users ? result.member_users.map((user) => user.user_id) : []; // should initially include the creator
    const externalUsers: number[] = [];
    pendingUsers.forEach((user) => {
      memberUsers.push(user.userId);
    });

    const memberUpdateSucceeded = yield* typedCall(
      updateFollowerGroupMembersSaga,
      updateFollowerGroupMembers(donorId, followerGroupId, memberUsers, externalUsers, followerGroup.muteNotification),
    );

    if (memberUpdateSucceeded) {
      yield put(setSagaMessage('', `${followerGroup.followerName} group and room created!`, ''));
    }
    createResult.memberUpdateError = !memberUpdateSucceeded;
    yield call(
      updateFollowerGroupTasksSaga,
      updateFollowerGroupTasks(donorId, followerGroupId, followerGroup.taskIds, followerGroup.muteNotification),
    );

    if (followerGroup.canUpdate) {
      yield call(
        updateFollowerGroupPermissionsSaga,
        updateFollowerGroupPermissions(donorId, followerGroupId, followerGroup.canUpdate),
      );
    }

    createResult.followerGroupId = followerGroupId;
  }

  return createResult;
}

export function* updateFollowerGroupSaga(action: UpdateFollowerGroup): SagaIterator<void> {
  const { donorId, followerGroup } = action;

  const { followerGroupId, followerName } = followerGroup;

  yield put(startLoading('follower'));

  const { error } = yield* typedCall(
    apiPut,
    api.txp.updateFollowerName,
    { followerId: followerGroupId },
    { name: followerName },
  );

  if (error || !followerGroupId) {
    yield put(pushError('Failed to update the follower name', error));
    yield put(setSagaMessage('', 'Updating follower failed', ''));
  } else {
    const donorState = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerGroupId];
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        ...donorFollowerGroup,
        name: followerName,
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));

    // After receiving the group, do the other update tasks
    if (donorFollowerGroup.canUpdate !== followerGroup.canUpdate) {
      // Use call instead of put to ensure the saga runs to completion before continuing
      // Previously this could fire concurrent update operations on the server
      // (updateFollowerGroupTasks) which both update permissions, resulting
      // in a race condition that could potentially throw an error
      yield call(
        updateFollowerGroupPermissionsSaga,
        updateFollowerGroupPermissions(donorId, followerGroupId, followerGroup.canUpdate),
      );
    }

    // Figure out what users to add/remove
    const { pendingUsers, pendingRemovedUsers } = followerGroup;
    // Get the original users for comparison
    const originalMemberUserIds = new Set((donorFollowerGroup.memberUsers || []).map((mu) => mu.userId));
    const originalExternalUserIds = new Set((donorFollowerGroup.externalUsers || []).map((eu) => eu.userId));

    const memberUsersToUpdate = [...originalMemberUserIds];
    const externalUsersToUpdate = [...originalExternalUserIds];

    let membersChanged = false;

    // Add users
    for (let i = 0; i < pendingUsers.length; i += 1) {
      const selectedContact = pendingUsers[i];
      if (!originalMemberUserIds.has(selectedContact.userId)) {
        if (!memberUsersToUpdate.includes(selectedContact.userId)) {
          memberUsersToUpdate.push(selectedContact.userId);
          membersChanged = true;
        }
      }
    }

    // Remove users
    for (let i = 0; i < pendingRemovedUsers.length; i += 1) {
      const removedUser = pendingRemovedUsers[i];
      if (removedUser.isExternalUser) {
        const index = externalUsersToUpdate.indexOf(removedUser.memberId);
        if (index !== -1) {
          externalUsersToUpdate.splice(index, 1);
          membersChanged = true;
        }
      } else {
        const index = memberUsersToUpdate.indexOf(removedUser.memberId);
        if (index !== -1) {
          memberUsersToUpdate.splice(index, 1);
          membersChanged = true;
        }
      }
    }

    // only send the request if group members changed
    if (membersChanged) {
      const updateFollowerGroupMembersResult = yield* typedCall(
        updateFollowerGroupMembersSaga,
        updateFollowerGroupMembers(
          donorId,
          followerGroupId,
          memberUsersToUpdate,
          externalUsersToUpdate,
          followerGroup.muteNotification,
        ),
      );
      if (!updateFollowerGroupMembersResult) {
        yield put(pushError('Failed to update follower members'));
        yield put(setSagaMessage('', 'Updating follower failed', ''));
      } else {
        yield put(setSagaMessage('', `${followerName || 'Follower group'} users updated!`, ''));
      }
    }

    // Send request to update tasks
    yield call(
      updateFollowerGroupTasksSaga,
      updateFollowerGroupTasks(donorId, followerGroupId, followerGroup.taskIds, followerGroup.muteNotification),
    );

    // Reset the follower edit state
    yield put(resetFollowerEditData());
    yield put(setSagaMessage('', `${followerName || 'Follower'} updated!`, ''));
  }

  // After updating everything, change the donor view the general status view
  yield put(selectDonorView(DONOR_STATUS_VIEW));
  yield put(finishLoading('follower'));
}

// Join a follower group. If the group is already joined, the user leaves the group instead.
export function* joinFollowerGroupSaga(action: JoinFollowerGroup): SagaIterator<void> {
  const { donorId, followerGroupId } = action;

  // initEditState: false - this fetch only needs fresh member data to compute the toggle; the
  // saga resets followerEdit below regardless
  const getFollowerGroupAction: GetFollowerGroup = {
    type: 'Donor/GET_FOLLOWER_GROUP',
    donorId,
    followerGroupId,
    initEditState: false,
  };
  yield put(startLoading('follower'));

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  // Get the follower groups
  yield call(getFollowerGroupSaga, getFollowerGroupAction);

  const profile = yield* typedSelect((state: RootState) => state.auth.profile);
  const followerGroups = yield* typedSelect((state: RootState) => state.donor.donors[donorId].followerGroups);
  const followerGroup = followerGroups ? followerGroups[followerGroupId] : undefined;
  if (!followerGroup) {
    yield put(setSagaMessage('', 'Joining the group failed', ''));
    yield put(finishLoading('follower'));
    return;
  }

  const memberUsers = (followerGroup.memberUsers || []).map((memberUser) => memberUser.userId);
  const externalUsers = (followerGroup.externalUsers || []).map((externalUser) => externalUser.userId);
  let isUserJoining = false;

  // Add yourself to the follower group, remove yourself if already there
  // Don't do anything if profile is undefined on state for some reason
  if (profile && !memberUsers.includes(profile.userId)) {
    memberUsers.push(profile.userId);
    isUserJoining = true;
  } else if (profile) {
    const index = memberUsers.indexOf(profile.userId);
    if (index !== -1) {
      memberUsers.splice(index, 1);
    }
  }

  // Send request to update users
  yield call(
    updateFollowerGroupMembersSaga,
    updateFollowerGroupMembers(donorId, followerGroupId, memberUsers, externalUsers),
  );

  // Reset the follower edit state
  yield put(resetFollowerEditData());
  yield put(setSagaMessage('', isUserJoining ? 'Joined Follower Group' : 'Left Follower Group', '', false, true));
  yield put(finishLoading('follower'));
}

export function* updateFollowerGroupTasksSaga(action: UpdateFollowerGroupTasks): SagaIterator<void> {
  const { donorId, followerGroupId, taskIds, muteNotification } = action;

  yield put(startLoading('followerTasks'));

  const { error } = yield* typedCall(
    apiPut<ApiUpdateFollowerTasksResponse>,
    api.txp.updateFollowerTasks,
    {
      followerId: followerGroupId,
    },
    {
      task_ids: taskIds,
      mute_notification: muteNotification,
    },
  );

  if (error) {
    yield put(pushError('Failed to update the follower name', error));
    yield put(setSagaMessage('', 'Updating follower tasks failed', ''));
  } else {
    const donorState = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerGroupId] || {};
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        ...donorFollowerGroup,
        taskIds,
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
  }

  yield put(finishLoading('followerTasks'));
}

export function* updateFollowerGroupMembersSaga(action: UpdateFollowerGroupMembers): SagaIterator<boolean> {
  yield put(startLoading('followerPeople'));

  const { donorId, followerGroupId, memberUserIds, externalUserIds, muteNotification } = action;

  const { result, error } = yield* typedCall(
    apiPut<ApiUpdateFollowerUsersResponse>,
    api.txp.updateFollowerUsers,
    {
      followerId: followerGroupId,
    },
    {
      user_ids: memberUserIds,
      external_user_ids: externalUserIds,
      mute_notification: muteNotification,
    },
  );

  if (error || !result) {
    yield put(pushError('Failed to update the follower members', error));
    yield put(setSagaMessage('', 'Updating follower members failed', ''));
  } else {
    const donorState = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerGroupId] || {};
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        ...donorFollowerGroup,
        memberUsers: result.member_users ? result.member_users.map((user) => parseProfile(user)) : [],
        externalUsers: result.external_users ? result.external_users.map((user) => parseExternalUser(user)) : [],
        invitedUsers: result.invited_users ? result.invited_users.map((invite) => parseFollowerInvite(invite)) : [],
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
  }

  yield put(finishLoading('followerPeople'));

  return error == null;
}

export function* updateFollowerGroupPermissionsSaga(action: UpdateFollowerGroupPermissions): SagaIterator<void> {
  const { donorId, followerGroupId, canUpdate } = action;

  const { result, error } = yield* typedCall(
    apiPut<ApiUpdateFollowerPermissionsResponse>,
    api.txp.updateFollowerPermissions,
    {
      followerId: followerGroupId,
    },
    {
      can_update: canUpdate,
    },
  );

  if (error || !result) {
    yield put(pushError('Failed to update the follower group permissions', error));
    yield put(setSagaMessage('', 'Updating follower permissions failed', ''));
  } else {
    const donorState = yield* typedSelect((state) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerGroupId] || {};
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerGroupId]: {
        ...donorFollowerGroup,
        canUpdate: result.can_update,
      },
    };
    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
    yield put(setSagaMessage('', `${donorFollowerGroup.name || 'Follower group'} permissions updated!`, ''));
  }
}

export function* deleteFollowerGroupSaga(action: DeleteFollowerGroup): SagaIterator<void> {
  const { donorId, followerGroupId } = action;

  yield put(startLoading('followers'));

  const { error } = yield* typedCall(apiDelete, api.txp.follower, {
    followerId: followerGroupId,
  });

  if (error) {
    yield put(pushError(`Failed to delete follower group ${followerGroupId}:`, error));
    yield put(setSagaMessage('', 'Failed to delete follower', ''));
  } else {
    const donorState = yield select((state) => state.donor.donors[donorId]);
    const donorFollowerGroups = {
      ...donorState.followerGroups,
    };
    delete donorFollowerGroups[followerGroupId];

    yield put(receiveDonorFollowerGroups(donorId, donorFollowerGroups));
    yield put(setSagaMessage('', 'Follower deleted!', ''));
  }

  yield put(finishLoading('followers'));
}

export function* removeUserFromFollowerGroupSaga(action: RemoveUserFromFollowers): SagaIterator<void> {
  const { donorId, followerId, memberId } = action;

  yield put(startLoading('followerPeople'));

  const { result, error } = yield* typedCall(
    apiDelete<ApiUpdateFollowerUsersResponse>,
    api.txp.updateFollowerRemoveUser,
    {
      followerId,
      userId: memberId,
    },
  );

  if (error || !result) {
    yield put(pushError(`Failed to remove user ${memberId} from follower ${followerId}:`, error));
    yield put(setSagaMessage('', 'There was a problem removing yourself from the Follower Group', ''));
  } else {
    const donorState = yield select((state) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerId] || {};
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerId]: {
        ...donorFollowerGroup,
        memberUsers: result.member_users ? result.member_users.map((user) => parseProfile(user)) : [],
        externalUsers: result.external_users ? result.external_users.map((user) => parseExternalUser(user)) : [],
        invitedUsers: result.invited_users
          ? result.invited_users.map((invite) => parseFollowerInvite(invite))
          : donorFollowerGroup.invitedUsers || [],
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
    yield put(setSagaMessage('', `You left the Follower group ${donorFollowerGroup.name || 'Follower group'}`, ''));
  }

  yield put(finishLoading('followerPeople'));
}

export function* deleteFollowerInviteSaga(action: DeleteFollowerInvite): SagaIterator<void> {
  const { donorId, followerId, followerInviteId } = action;

  const { result, error } = yield* typedCall(apiDelete<ApiDeleteFollowerInviteResponse>, api.txp.deleteFollowerInvite, {
    followerId,
    followerInviteId,
  });

  if (error || !result) {
    yield put(pushError('Failed to remove invited user', error));
    yield put(setSagaMessage('', 'Failed to remove invited user', ''));
  } else {
    const donorState = yield* typedSelect((state: RootState) => state.donor.donors[donorId]);
    const donorFollowerGroups = donorState.followerGroups ? donorState.followerGroups : {};
    const donorFollowerGroup = donorFollowerGroups[followerId] || {};
    const updatedFollowerGroupsMap = {
      ...donorFollowerGroups,
      [followerId]: {
        ...donorFollowerGroup,
        memberUsers: result.member_users ? result.member_users.map((user) => parseProfile(user)) : [],
        externalUsers: result.external_users ? result.external_users.map((user) => parseExternalUser(user)) : [],
        invitedUsers: result.invited_users ? result.invited_users.map((invite) => parseFollowerInvite(invite)) : [],
      },
    };

    yield put(receiveDonorFollowerGroups(donorId, updatedFollowerGroupsMap));
  }
}

export function* getCaseTimelineSaga(action: GetCaseTimeline): SagaIterator<void> {
  const { caseId } = action;

  yield put(startLoading('caseTimeline'));

  const { result, error } = yield* typedCall(apiFetch<ApiGetCaseTimelineResponse>, api.txp.caseTimeline, { caseId });

  if (error || !result) {
    yield put(pushError(`Failed to load timeline for case ${caseId}:`, error));
  } else {
    const timeline = result.timeline.map((entry) => ({
      type: entry.type,
      timestamp: entry.timestamp,
      userId: entry.user_id,
      taskId: entry.task_id,
      taskName: entry.task_name,
      noteContent: entry.note_content,
      fieldId: entry.field_id,
      fieldLabel: entry.field_label,
      sectionName: entry.section_name,
    }));
    yield put(receiveCaseTimeline(caseId, timeline));
  }

  yield put(finishLoading('caseTimeline'));
}

function isAddPeopleByEmailAction(action: AddPeopleByEmail | AddPeopleByPhone): action is AddPeopleByEmail {
  return 'email' in action && typeof action.email === 'string';
}

function isAddPeopleByPhoneAction(action: AddPeopleByEmail | AddPeopleByPhone): action is AddPeopleByPhone {
  return 'phone' in action && typeof action.phone === 'string';
}

export function* checkContactIsTxpUserSaga(action: AddPeopleByEmail | AddPeopleByPhone): SagaIterator<void> {
  const email = isAddPeopleByEmailAction(action) ? action.email.toLowerCase() : undefined;
  const phone = isAddPeopleByPhoneAction(action) ? serializePhone(action.phone) : undefined;

  yield put(startLoading('checkContact'));

  const { result, error } = yield* typedCall(apiPost<ApiContactsResponse>, api.txp.getContacts, undefined, {
    email_list: email ? [email] : [],
    phone_list: phone ? [phone] : [],
  });

  let user: ApiUserPublicProfile | undefined;

  if (!error && result) {
    const localContacts = result.local_contacts;
    const chatroomContacts = result.chatroom_contacts;
    const donorContacts = result.donor_contacts;
    const organizationContacts = result.organization_contacts;
    // const directoryContacts = result.directory_contacts;
    // const excludedContacts = result.excluded_contacts;
    // const externalContacts = result.external_contacts;
    const publicContacts = result.public_contacts;

    const finder = email
      ? (contact: ApiUserPublicProfile) => contact.email?.toLowerCase() === email
      : (contact: ApiUserPublicProfile) => contact.phone === phone;

    user = [...localContacts, ...chatroomContacts, ...donorContacts, ...organizationContacts, ...publicContacts].find(
      finder,
    );
  }

  if (error || !user) {
    const contactType = email ? 'email address' : 'phone number';
    const message = `The ${contactType} does not match a current FlowHawk user`;

    yield put(pushError(`${message}:`, error));
    yield put(setSagaMessage('', message, ''));
    yield put(setAddPeopleError(message));
    yield put(addSingleUserToChatroomFailed('Not a registered user'));
  } else {
    const users = yield* typedSelect((state: RootState) => state.addPeople.selectedUsers);

    yield put(
      setSelectedUsers([
        ...users,
        {
          userId: user.user_id,
          email: user.email,
          phone: user.phone,
          firstName: user.first_name,
          lastName: user.last_name,
          profilePicture: user.presigned_profile_picture_url,
          profilePictureThumbnail: user.presigned_profile_picture_thumbnail_url,
          organizationName: ' ',
        },
      ]),
    );
  }

  yield put(finishLoading('checkContact'));
}

export function* inviteUserSaga(action: AddPeopleInvite): SagaIterator<void> {
  const { email, phone, firstName, lastName, organization, organizationId, department } = action;

  const profileName = yield* typedSelect((state: RootState) =>
    state.auth.profile ? selectProfileName(state.auth.profile) : 'FlowHawk User',
  );
  const payload: ApiInviteUserRequest = {
    sender_information: profileName,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    organization: organization || undefined,
    department: department || undefined,
    org_id: organizationId,
  };
  payload.email = email || undefined;
  payload.phone = phone || undefined;
  const { error } = yield* typedCall(apiPost<ApiInviteUserResponse>, api.txp.inviteNewUser, undefined, payload);

  if (error) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        yield put(inviteNewUserFailed('Validation failed while attempting to invite user.'));
      } else if (error.isNetworkError) {
        yield put(inviteNewUserFailed('Request failed - are you offline?'));
      } else {
        // Probably a malformed request in some way.
        yield put(inviteNewUserFailed('An unexpected error occurred.'));
      }
    } else {
      // Probably a malformed request in some way.
      yield put(inviteNewUserFailed('An unexpected error occurred.'));
    }
  } else {
    yield put(inviteNewUserSuccess());
    yield put(setSagaMessage('', 'New user invite sent!', '', undefined, true));
  }
}

export function* inviteNewUserSaga(action: InviteNewUser): SagaIterator<void> {
  const { followerId, chatId, memberId, email, phone, firstName, lastName, organization, organizationId, department } =
    action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  // use an unsealed object to prevent flow errors in adding either phone or both
  const profileName = yield* typedSelect((state: RootState) =>
    state.auth.profile ? selectProfileName(state.auth.profile) : 'FlowHawk User',
  );
  const payload: ApiInviteUserRequest = {
    sender_information: profileName,
    follower_id: followerId,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    organization: organization || undefined,
    department: department || undefined,
    org_id: organizationId,
  };

  payload.email = email || undefined;
  payload.phone = phone || undefined;
  const { error } = yield* typedCall(apiPost<ApiInviteUserResponse>, api.txp.inviteNewUser, undefined, payload);

  if (error) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        yield put(inviteNewUserFailed(parseError(error)));
      } else if (error instanceof InvalidResponseCode) {
        yield put(inviteNewUserFailed(parseResponseTextError(error.responseText)));
      } else if (error.isNetworkError) {
        yield put(inviteNewUserFailed('Request failed - are you offline?'));
      } else {
        // Probably a malformed request in some way.
        yield put(inviteNewUserFailed('An unexpected error occurred.'));
      }
    }
  } else {
    yield put(inviteNewUserSuccess());
    yield put(setSagaMessage('', 'New user invite sent!', ''));
    yield put(loadMessages(chatId, memberId, true, 0));

    // Re-fetch the follower group so the new invite appears in the invited users list
    const donorId = yield* typedSelect((state: RootState) => state.donor.openDonorId);
    if (donorId && followerId) {
      const getFollowerGroupAction: GetFollowerGroup = {
        type: 'Donor/GET_FOLLOWER_GROUP',
        donorId,
        followerGroupId: followerId,
        initEditState: false,
      };
      yield put(startLoading('follower'));
      yield call(getFollowerGroupSaga, getFollowerGroupAction);
      yield put(finishLoading('follower'));
    }
  }
}

export function* setEditCaseTaskStatusSaga(action: SetEditCaseTaskStatus): SagaIterator<void> {
  const { caseId, taskId, editBy } = action;

  const { result, error } = yield* typedCall(
    apiPut<ApiEditCaseTaskResponse>,
    api.txp.editCaseTask,
    {
      caseId,
      taskId,
    },
    {
      edit_by: editBy || null,
    },
  );

  if (result) {
    yield put(receiveEditCaseTaskStatus(result.case_id, result.task_id, result.edit_by, result.edit_time));
  } else {
    yield put(pushError('Failed to set edit status for task', error));
  }
}

export function* generateModularCasePDFSaga(action: GenerateModularCasePDF): SagaIterator<void> {
  const { caseId, sections } = action;
  yield put(startLoading('generatePDF'));

  devLog('lifecycle', [`Generating PDF for case ${caseId}`, sections]);

  const body = {
    includeDetails: sections.caseDetails.include,
    includeTags: Boolean(sections.caseDetails.detailKeys?.includes('tags')),
    includeNotes: Boolean(sections.caseDetails.detailKeys?.includes('notes')),
    includeTimeline: Boolean(sections.caseDetails.detailKeys?.includes('timeline')),
    includeAttachments: sections.caseAttachments.include,
    includeTasks: sections.caseTasks.include,
    includeChatrooms: sections.caseChatrooms.include,
    includeImageAppendix: sections.caseImageAppendix.include,
    task_ids: sections.caseTasks.taskIds || [],
    chatroom_ids: sections.caseChatrooms.chatroomIds || [],
  };
  // POST to /cases/:case_id/pdf with JSON body and expect arraybuffer
  try {
    const { result, error } = yield* typedCall(
      apiPost<ApiGenerateModulePdfResponse>,
      api.txp.generateModularCasePdf,
      { caseId },
      body,
    );

    devLog('lifecycle', [`PDF generation result for case ${result}`]);

    if (!error && result) {
      const { pdfBase64 } = result;
      // Decode Base64 into binary string
      const binary = atob(pdfBase64);
      // Create a Uint8Array from that
      const len = binary.length;
      const array = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        array[i] = binary.charCodeAt(i);
      }
      // Build the Blob and open it
      const blob = new Blob([array], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      yield put(pushError(`Failed to generate PDF for case ${caseId}`, error));
    }

    yield put(finishLoading('generatePDF'));
  } catch (error) {
    const newError = error instanceof Error ? error : new Error('Unknown pdf error');
    yield put(pushError('Failed to generate pdf', newError));
    yield put(finishLoading('generatePDF'));
  }
}

export function* pinCaseSaga(action: PinCase) {
  const { caseId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCaseCommandNoResult(caseId, memberId, 'pin-case', [caseId]);
}

export function* unpinCaseSaga(action: UnpinCase) {
  const { caseId } = action;

  const memberId = yield* typedSelect((state) => selectUserId(state.auth));
  yield sendCaseCommandNoResult(caseId, memberId, 'unpin-case', [caseId]);
}

export function* createCaseViewSaga(action: CreateCaseView): SagaIterator<void> {
  const { params } = action;

  const { result, error } = yield* typedCall(apiPost<CaseView>, api.txp.createCaseView, undefined, {
    organizationId: params.organizationId,
    name: params.name,
    view: params.view,
  });

  if (error || !result) {
    const errorMessage = 'Failed to create case list';
    yield put(createCaseViewFailed(errorMessage));
    yield put(setSagaMessage('', errorMessage, ''));
    return;
  }

  yield put(createCaseViewSuccess(result));
  yield call(refetchCaseViews);
  const currentSessionFilter = yield* typedSelect((state: RootState) => state.auth.sessionCaseFilter);
  yield put(
    storeSessionCaseFilter({
      ...currentSessionFilter,
      selectedCaseViewId: result.id,
    }),
  );
  yield put(setSagaMessage('Case list created successfully', '', ''));
}

export function* updateCaseViewSaga(action: UpdateCaseView): SagaIterator<void> {
  const { params } = action;

  const { result, error } = yield* typedCall(
    apiPut<CaseView>,
    api.txp.updateCaseView,
    { casesViewId: params.casesViewId },
    {
      organizationId: params.organizationId,
      name: params.name,
      view: params.view,
    },
  );

  if (error || !result) {
    const errorMessage = 'Failed to update case list';
    yield put(updateCaseViewFailed(errorMessage));
    yield put(setSagaMessage('', errorMessage, ''));
    return;
  }

  yield put(updateCaseViewSuccess(result));
  yield call(refetchCaseViews);
  const currentSessionFilter = yield* typedSelect((state: RootState) => state.auth.sessionCaseFilter);
  yield put(
    storeSessionCaseFilter({
      ...currentSessionFilter,
      selectedCaseViewId: result.id,
    }),
  );
  yield put(setSagaMessage('Case view updated successfully', '', ''));
}

export function* deleteCaseViewSaga(action: DeleteCaseView): SagaIterator<void> {
  const { casesViewId } = action;

  const { error } = yield* typedCall(apiDelete, api.txp.deleteCaseView, { casesViewId });

  if (error) {
    const errorMessage = 'Failed to delete case list';
    yield put(deleteCaseViewFailed(errorMessage));
    yield put(setSagaMessage('', errorMessage, ''));
    return;
  }

  yield put(deleteCaseViewSuccess(casesViewId));
  yield call(refetchCaseViews);
  const currentSessionFilter = yield* typedSelect((state: RootState) => state.auth.sessionCaseFilter);
  yield put(
    storeSessionCaseFilter({
      ...currentSessionFilter,
      selectedCaseViewId: -1,
    }),
  );
  yield put(setSagaMessage('Case view deleted successfully', '', ''));
}
